/* ============================================
   QUOTE DASHBOARD — Activity / Notes log card
   Asset: dashboard-activity-card.js

   Extracted from dashboard-main.js on 2026-05-23 as Phase B.2.e of the
   dashboard refactor — the biggest single panel extraction
   (~775 LOC moved out). This is the Universal Notes / Activity card
   that renders for EVERY non-Pending detail panel + for PENDING rows
   as well (renamed "Notes" → "Activity" 2026-05-16).

   What it renders:
     - Composer: 5-button type-chip stack (Note / Call / Email / Meeting /
       Drop-In) with optional "New Email" action button for Outlook-
       enabled reps, 3-line textarea, follow-up cluster (preset days +
       custom date picker), Save button with dynamic label flip to
       "Put on Hold" / "Edit Hold Date" on lead-state rows with an
       armed follow-up.
     - DEAL FEED entries loop (left 2/3): newest-first with structured
       rendering for system transitions (pill-pair from→to), Outlook emails
       (subject + from/to + preview + thread badge + internal/attachment
       chips), workflow events (sample-shipped, vendor-po, vendor-purchased,
       freight-cost, install-req, deal-closed, etc.), and rep-typed notes
       with combined transition support (rep note + auto-hold). Edit +
       delete buttons gated on admin OR rep-own-entry-within-30min;
       _SYSTEM/_AUTO/_OUTLOOK entries are read-only. Pagination at
       DEAL_FEED_DEFAULT_CAP=50; older entries collapsed behind
       "Show N older entries" button.
     - LINKED FILES sidecar (right 1/3): floor plans + future asset types
       linked via /quote-link-design. Inline Link Asset / Unlink buttons.

   Self-gating logic:
     - Collapse state from collapsedSections[ref].notes
     - Online orders with zero entries auto-collapse on initial render
     - PENDING rows are non-collapsible (only 2 cards on panel; chevron
       hidden, click router skips)
     - Lifecycle-aware follow-up preset shelf:
         NEW LEAD / SAMPLE / ON HOLD-with-timer: short shelf (+1..+5)
         RESUMED + ON HOLD-expired + QUOTED / SOLD: long shelf
         LOST: longest shelf (+14..+365, annual budget cycle case)

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildActivityCard(q, ctx) -> html string
       Returns the entire <div class="rqd-detail__card rqd-notes-log...">
       ...</div> block. Caller appends into its own html accumulator
       (typically right after buildDetailProgressBar(q) in buildDetailHtml).
       ctx.isPend supplies the PENDING flag from buildDetailHtml's local
       scope (controls non-collapsible card chrome).

   Init contract (RQD.initActivityCard called once by main.js at boot):
     esc, formatNoteDate, formatFollowUpDate, formatFollowUpRemaining,
     formatDate, _followUpDayCount, daysFromNowYmd — function
     declarations in main (some defined ~L14550+ but hoisted), safe to pass
     by reference per the init-deps-var-hoisting-trap memo.
     collapsedSections — object reference (shared mutable store; main
     also reads/writes it from the card-header click toggle ~L1985 and
     prospect detail ~L17097). Same by-reference pattern as initOrderCards.
     getIsAdmin — closure-getter. isAdmin is a  that mutates after
     Auth.getSession() resolves (~L91). Used to gate the entry-edit/delete
     button render path.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (2026-07-12) — Outlook is enabled for every
     non-agency rep now (the OUTLOOK_ENABLED_REPS allowlist is gone — see the
     worker's isOutlookEnabledRep). The composer shows the "New Email" compose
     button + hides the manual "email" note chip only once the rep has actually
     CONNECTED their mailbox, so we fetch /outlook-status once at load and cache
     the connected flag. Agency can never connect (OAuth is blocked server-side),
     so this also excludes them. Cards render on deal-row expand — seconds after
     load — so this resolves well before the gate is read. */
  var _outlookConnected = false;
  (function _initOutlookConnected() {
    try {
      if (window.RepPortal && window.RepPortal.API && window.RepPortal.API.fetch) {
        window.RepPortal.API.fetch('/outlook-status')
          .then(function(s) { _outlookConnected = !!(s && s.connected); })
          .catch(function() { _outlookConnected = false; });
      }
    } catch (e) { /* not signed in / no portal API — leave disabled */ }
  })();

  // Injected by RQD.initActivityCard (from main.js boot)
  var esc, formatNoteDate, formatFollowUpDate, formatFollowUpRemaining,
      formatDate, _followUpDayCount, daysFromNowYmd, collapsedSections, getIsAdmin, fetchQuotes;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-13) — uniform timestamp stack for
     the deal feed. Every entry type (note, transition, system, email) renders
     the timestamp as three right-aligned lines — date / time / author —
     instead of a single "Tue Jun 9, 11:14 am — RW" string. formatNoteDate
     returns "<date>, <time>" (one comma), so split once. byLabel is passed
     already-escaped (or empty, e.g. emails show sender in their address row).
     The .rqd-when CSS gives one tight proportional font everywhere — fixes the
     email path looking wider than notes (it used tabular-nums). */
  /* CUSTOM_FEEDFORMAT_BY_CLAUDE (Ryan 2026-09-12) — WHO LOGGED IT, in words a person uses.
     `_OUTLOOK` is the worker's internal actor for anything the mailbox sync wrote, and it was
     never translated — so entries that did not take the email branch printed the literal
     `_OUTLOOK` in the byline. ⛔ ONLY THE LABEL CHANGES: `e.by` stays `_OUTLOOK` on the record and
     in every predicate that keys off it (the read-only gate at `_isSystemStamped` is one), because
     renaming the stored actor would silently un-gate entries people cannot edit. */
  /* Ryan 2026-09-12, second pass: *"You made Call details, Call notes — both bold. And Follow-up is
     regular weight. Either all bold, or none."* All bold. The follow-up line is built as a string
     rather than assembled from spans like its two siblings, so the label is a shared constant —
     and it is shared with dashboard-deal-feed.js's optimistic copy through the same markup, so the
     just-saved entry and the persisted one cannot disagree about it. */
  var _FU_LABEL = '<span class="rqd-notes-log__entry-line-label">Follow-up:</span>';
  function _mailFilterTag(em) {
    /* CUSTOM_MAILFILTERTAG_BY_CLAUDE (Ryan 2026-10-01, RW-1451-A) — the email filter's warning as a
       small grey tag. The worker cuts the filter's yellow banner out of the body when it files the
       email and keeps what it said on emailMeta.mailFilter ({ by, title, tags }). */
    var mf = em && em.mailFilter;
    if (!mf) return '';
    var tags = Array.isArray(mf.tags) && mf.tags.length ? mf.tags : [mf.title || 'Flagged'];
    var tip = (mf.title || 'Flagged') + ' (' + (mf.by || 'mail filter') + '): ' + tags.join(', ');
    return '<span class="rqd-mailfilter-tag" title="' + esc(tip) + '">&#9888; Mail filter: ' + esc(tags.join(', ')) + '</span>';
  }
  function _byLabelFor(by) {
    if (by === '_AUTO') return 'Auto';
    if (by === '_SYSTEM') return 'System';
    if (by === '_OUTLOOK') return 'Email';
    return esc(by || '');
  }
  /* Ryan 2026-09-12: his rewording of the on-hold reply note, applied at DISPLAY so the entries
     already on a deal read the same as the ones written from now on. The writer changed too
     (rfq-worker outlook-sync.js) — this is the repair for what is already on record, the same
     arrangement the customer-name casing uses. Matched loosely on the distinctive half so a stored
     line with or without the emoji is caught. */
  function _repairNoteText(t) {
    var s = String(t == null ? '' : t);
    if (s.indexOf('replied while on hold') === -1) return s;
    return s.replace(/^\s*(?:✉️?\s*)?Customer replied while on hold.*$/,
      'Customer replied while on hold; countdown still running.');
  }
  function whenStackHtml(ts, byLabel, legacy, byKind) {
    var full = legacy ? 'Older notes' : (formatNoteDate(ts) || '');
    var dateLine = full, timeLine = '';
    var ci = full.indexOf(', ');
    if (ci !== -1) { dateLine = full.slice(0, ci); timeLine = full.slice(ci + 2); }
    /* CUSTOM_FEEDVIEW_BY_CLAUDE (Ryan 2026-10-10): a THREE-line stack (date / time / who) is marked
       so its third line may hang beside the text below instead of pushing that text a line down
       (dashboard.css .rqd-when--three). The stack itself is unchanged. */
    var h = '<span class="rqd-when' + (timeLine && byLabel ? ' rqd-when--three' : '') + '">';
    h += '<span class="rqd-when__date">' + esc(dateLine) + '</span>';
    if (timeLine) h += '<span class="rqd-when__time">' + esc(timeLine) + '</span>';
    if (byLabel) h += '<span class="rqd-when__by' + (byKind ? ' rqd-when__by--' + byKind : '') + '">' + byLabel + '</span>';
    return h + '</span>';
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — PENDING AUTOMATED EMAILS, FROZEN AT THE TOP
     ============================================
     Ryan: *"a frozen section at the top can be inserted showing automated pending emails with a
     date/time/copy of it/etc."*

     ⛔ AND IT SERVES THE SAMPLES LADDER TOO, which is the half he had to point out:
     *"the samples flow doesn't show you anything in the deal feed frozen at the top. good to add
     that in there too... all of these flows can follow the same sort of rules and whatnot."* That
     ladder has been sending customer email unattended since 2026-08-19 with NOTHING on the feed
     to say so. One block, one endpoint (/sequence-status), two providers.

     ⛔ THE SUMMARY IS SYNCHRONOUS AND THE COPY IS ONE CLICK. `q.pendingAutomation` rides the row
     — it comes off a KV list's key metadata, so the whole board costs two reads — which means
     WHICH email and WHEN are on screen the instant the card opens, with no spinner and no
     request. The prose is the expensive half (the samples ladder has to BUILD its next rung to
     show it), so it waits for someone to ask. Same bargain the deal feed already makes with
     email bodies.

     ⚠️ A HELD SEQUENCE STILL RENDERS, and says so. `holdKind` means the thing has stalled on
     something a human must fix — a disconnected mailbox, a reassignment — and that is precisely
     when a rep needs to see it. It is also why the bell starts ringing again for those deals
     (see isAutomationPending in the worker): held is not handled. */
  function buildSequenceBlock(q) {
    var pa = q && q.pendingAutomation;
    /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-26) — an automation with no DATE is still an
       automation. The post-delivery ladder gets its job (and its `sendAfter`) only when the
       carrier reports delivery, so this block — the one surface that exists to say what is
       coming — was blank for the entire time the box was in transit. It now renders the armed
       state and says what it is waiting for. */
    if (!pa) return '';
    var awaiting = !pa.sendAfter && pa.awaiting === 'delivery';
    if (!pa.sendAfter && !awaiting) return '';
    var ref = q.reference || '';
    var held = !!pa.holdKind;
    var when = awaiting
      ? ('waiting on ' + (pa.carrier ? pa.carrier : 'the carrier'))
      : (pa.whenText ? String(pa.whenText) : _seqWhenLabel(pa.sendAfter)); // whenText: CUSTOM_CHECKINBLOCK_BY_CLAUDE - an email parked on a [TEST] says who it waits on
    var of = Number(pa.of) || 0;
    var rung = Number(pa.rung) || 0;
    /* ⚠️ "email 1 of 4" is a claim about a QUEUE. While we are only waiting on a carrier there is
       no queue yet, so it says what is true instead: how many are coming once the box lands. */
    var counted = awaiting
      ? (of ? (' · ' + of + ' emails, from the day after delivery') : '')
      : ((of && rung) ? (' · email ' + rung + ' of ' + of) : '');

    var h = '<div class="rqd-seq-block' + (held ? ' rqd-seq-block--held' : '') + '" data-seq-ref="' + esc(ref) + '">';
    h +=   '<div class="rqd-seq-block__head">';
    h +=     '<span class="rqd-seq-block__icon" aria-hidden="true">' + (held ? '⏸' : '⚡') + '</span>';
    h +=     '<span class="rqd-seq-block__title">' + esc(pa.label || 'Automated emails') + counted + '</span>';
    h +=     '<span class="rqd-seq-block__when">' + esc(held ? 'on hold' : when) + '</span>';
    /* ⛔ NOTHING TO SHOW YET. The rungs are BUILT at send time from the delivery date, so there
       is no frozen copy to open while the box is still moving — the button would fetch an empty
       pane and read as broken. Stop is still offered: a rep who does not want this ladder should
       not have to wait for a delivery to say so. */
    if (!awaiting) {
      h +=   '<button type="button" class="rqd-seq-block__btn" data-seq-show="' + esc(ref) + '">Show the copy</button>';
    }
    /* ⛔ ONE BUTTON, AND IT STOPS THE WHOLE THING. Ryan chose view + Stop: no per-rung skip, no
       editing in place. A rep who wants a different email writes one, which also stops the
       sequence (a manual send holds it, a reply or a call closes it). */
    h +=     '<button type="button" class="rqd-seq-block__btn rqd-seq-block__btn--stop" data-seq-stop="' + esc(ref) + '">Stop these</button>';
    h +=   '</div>';
    if (held) {
      h += '<div class="rqd-seq-block__hold">' + esc(pa.holdReason || 'Waiting on something before the next email can send.') + '</div>';
    }
    h +=   '<div class="rqd-seq-block__body" data-seq-body="' + esc(ref) + '" hidden></div>';
    h += '</div>';
    return h;
  }

  /* ============================================
     CUSTOM_CALLWAIT_BY_CLAUDE (AIOPS 5f9a062bf0da, Ryan 2026-09-09) — A DEAL PARKED UNTIL THE
     CUSTOMER'S CALL
     ============================================
     Some lanes stop on Ryan's call (Legend freight-quoted ≥ $1k: call first, then Rocket, then
     Adjust). The agent parks the DEAL (POST /call-wait) and the row carries `q.callWait`; when a
     call is logged here with any outcome but no-answer the worker wakes the agent and answers
     `callWait.woken` on the note save, and this block is repainted on that click.
     THREE SURFACES: the block is what anyone SEES; Park / Clear are the admin's own path when the
     agent is down; the agent's path is the route. Non-admins see the state and no buttons.
     ⚠️ DOM-ONLY REPAINTS + a quiet refetch, the sequence block's pattern: this module cannot
     reach allQuotes, so after Park / Clear the block is rebuilt from the response and the row is
     refreshed by fetchQuotes. */
  var _CW_LANES = [
    ['rocket_quote', 'Rocket freight quote'], ['adjust_collect', 'Adjust Order balance collect'],
    ['request_freight', 'Supplier freight ask'], ['request_dims', 'Supplier dims ask'],
    ['send_po', 'Send the PO'], ['buy_web_order', 'Web-order buy'], ['freight_apply', 'Apply freight to the quote'],
    ['samples_request', 'Samples request'], ['review', 'Review the deal'],
  ];
  function _cwLaneLabel(lane) {
    if (!lane) return 'the next step';
    for (var i = 0; i < _CW_LANES.length; i++) if (_CW_LANES[i][0] === lane) return _CW_LANES[i][1];
    return String(lane).replace(/_/g, ' ');
  }
  function _cwFormHtml(ref) {
    var h = '<div class="rqd-callwait-form" data-callwait-form="' + esc(ref) + '" hidden>';
    h +=   '<input type="text" class="rqd-callwait-form__reason" maxlength="300" placeholder="Why (e.g. Legend freight $798, call before Rocket)">';
    h +=   '<select class="rqd-callwait-form__lane"><option value="">After the call…</option>';
    _CW_LANES.forEach(function (l) { h += '<option value="' + esc(l[0]) + '">' + esc(l[1]) + '</option>'; });
    h +=   '</select>';
    h +=   '<button type="button" class="rqd-seq-block__btn rqd-callwait-form__save" data-callwait-save="' + esc(ref) + '">Park</button>';
    h +=   '<button type="button" class="rqd-seq-block__btn" data-callwait-cancel="' + esc(ref) + '">Cancel</button>';
    h += '</div>';
    return h;
  }
  function buildCallWaitBlock(q) {
    var ref = (q && q.reference) || '';
    if (!ref) return '';
    var cw = q && q.callWait;
    var waiting = !!(cw && cw.status === 'waiting');
    var admin = getIsAdmin();
    /* Nothing parked and not an admin: nothing to show, and nothing to press. */
    if (!waiting && !admin) return '';
    /* CUSTOM_CALLWAITUI_BY_CLAUDE (Ryan 2026-09-12) — ⛔ THE BLOCK SAID NOTHING A PERSON COULD ACT
       ON. It read "Waiting on your call before the agent continues?" over a button called "Park
       until my call", and Ryan's verdict was *"I have NO idea what it does"* — fair, because the
       words describe the mechanism (a park) rather than the effect (the AI agent stops touching
       this deal until you have phoned the customer). Both states now say the effect first, then
       how it ends. The unpark button is "Unpark", not "Clear": clear reads like clearing a
       notification, and this one changes what a bot will do overnight. */
    var h = '<div class="rqd-seq-block rqd-seq-block--callwait' + (waiting ? '' : ' rqd-seq-block--callwait-idle') + '" data-callwait-ref="' + esc(ref) + '">';
    h +=   '<div class="rqd-seq-block__head">';
    if (waiting) {
      h +=   '<span class="rqd-seq-block__icon" aria-hidden="true">📵</span>';
      h +=   '<span class="rqd-seq-block__title">Paused — the AI agent is waiting for you to call this customer</span>';
      h +=   '<span class="rqd-seq-block__when">then it does: ' + esc(_cwLaneLabel(cw.nextExpectedLane)) + '</span>';
      if (admin) h += '<button type="button" class="rqd-seq-block__btn rqd-seq-block__btn--stop" data-callwait-clear="' + esc(ref) + '">Unpause the agent</button>';
    } else {
      h +=   '<span class="rqd-seq-block__icon" aria-hidden="true">📵</span>';
      h +=   '<span class="rqd-seq-block__title rqd-seq-block__title--muted">Need to call this customer before the AI agent does anything else?</span>';
      h +=   '<button type="button" class="rqd-seq-block__btn" data-callwait-park="' + esc(ref) + '">Pause the agent until I call</button>';
    }
    h +=   '</div>';
    if (waiting) {
      h += '<div class="rqd-seq-block__hold">'
        + 'It will not work this deal until you log a call below. Anything except a no-answer counts, '
        + 'and the agent picks up again the moment you save it.'
        + (cw.reason ? ' <span class="rqd-callwait-why">Why: ' + esc(cw.reason) + '</span>' : '')
        + (cw.setBy ? ' <span class="rqd-callwait-by">Paused by ' + esc(cw.setBy) + '.</span>' : '') + '</div>';
    } else {
      h += '<div class="rqd-seq-block__hold">'
        + 'Pausing stops the AI agent working this deal. It starts again as soon as you log a call '
        + 'here (anything except a no-answer). Nothing is emailed either way.</div>';
      h += _cwFormHtml(ref);
    }
    h += '</div>';
    return h;
  }
  /* CUSTOM_FOLLOWUPS_BY_CLAUDE (AIOPS `deadff7115ea`, Ryan 2026-09-29) - WHO WE ARE WAITING ON. An agent that sends an
     important email sets a follow-up time; until somebody answers on that thread, the deal says so here, above the
     feed like the call-wait block (Ryan's pick: a chip on the deal AND the feed lines). Read only - the agent chases
     or closes it; a reply clears it by itself. It borrows the call-wait slab (`--cw-*` vars per status) so it reads
     on every card colour without a palette of its own. `q.followUps` comes from GET /rep-quotes (worker
     lib/followups.js `openFollowupsByRef`); absent means none open. */
  function buildFollowUpBlock(q) {
    var list = q && Array.isArray(q.followUps) ? q.followUps : null;
    if (!list || !list.length) return '';
    var h = '<div class="rqd-seq-block rqd-seq-block--callwait rqd-seq-block--followup">';
    for (var i = 0; i < list.length; i++) {
      var fu = list[i] || {};
      var who = fu.who || 'a reply';
      h += '<div class="rqd-seq-block__head">';
      h +=   '<span class="rqd-seq-block__icon" aria-hidden="true">' + (fu.overdue ? '⏰' : '⏳') + '</span>';
      h +=   '<span class="rqd-seq-block__title">' + (fu.overdue ? 'Follow-up due, no reply yet from ' : 'Waiting on ') + esc(who) + (fu.subject ? ' (' + esc(fu.subject) + ')' : '') + '</span>';
      h +=   '<span class="rqd-seq-block__when">' + (fu.overdue ? 'was due ' : 'follow up by ') + esc(fu.dueLabel || '') + (fu.seat ? ' · ' + esc(fu.seat) : '') + '</span>';
      h += '</div>';
    }
    h += '</div>';
    return h;
  }
  /* CUSTOM_ABBYINTRO_BY_CLAUDE (AIOPS 67db43d9ee42, Ryan 2026-09-30) - ABBY'S INTRO ON A SOLD OFFLINE DEAL. Shows what
     the intro did (scheduled / sent to Ryan as a TEST / sent / rode inside the paid invoice / not sent, and why) and the
     rep's switch "I'm handling the customer emails on this deal", which stops it any time until it sends (Ryan's pick:
     the deal card, not the Mark-as-Sold dialog). Saves through /quote-lifecycle {abbyIntroOptOut}. `q.abbyIntro` comes
     from the worker's quote-summary-builder; absent on a deal the switch has never touched. Offline sold deals only. */
  var _AI_STATUS = {
    scheduled: 'scheduled', 'test-staged': 'sent to Ryan as a [TEST] first', sent: 'sent',
    merged: 'sent inside the paid invoice', skipped: 'not sent',
  };
  function buildAbbyIntroBlock(q) {
    var ref = (q && q.reference) || '';
    if (!ref || q.source === 'online' || !/^[A-Z]{2,4}-\d{3,6}-[A-Z]$/.test(ref)) return '';
    /* Only once an intro exists for this deal (the sold flip wrote one, or a rep ticked the switch). With the feature
       off nothing is ever written, so old sold deals show nothing - an opt-out for an email that will never go is noise. */
    var ai = q.abbyIntro || null;
    if (!ai || (!ai.status && !ai.optOut)) return '';
    var st = ai && ai.status;
    var done = st === 'sent' || st === 'merged';
    var optOut = !!(ai && ai.optOut);
    var line = st ? ("Abby's intro: " + (_AI_STATUS[st] || st)) : "Abby's intro to the customer";
    if (st === 'scheduled' && ai.sendAfter) {
      try { line += ' for ' + new Date(ai.sendAfter).toLocaleString('en-US', { timeZone: 'America/Denver', weekday: 'short', hour: 'numeric', minute: '2-digit' }) + ' MT'; } catch (_) {}
    }
    if (st === 'skipped' && ai.reason) line += ' (' + String(ai.reason).replace(/-/g, ' ') + ')';
    var h = '<div class="rqd-seq-block rqd-seq-block--callwait rqd-seq-block--callwait-idle rqd-seq-block--abbyintro" data-abbyintro-ref="' + esc(ref) + '">';
    h +=   '<div class="rqd-seq-block__head">';
    h +=     '<span class="rqd-seq-block__icon" aria-hidden="true">✉️</span>';
    h +=     '<span class="rqd-seq-block__title' + (done ? '' : ' rqd-seq-block__title--muted') + '">' + esc(line) + '</span>';
    h +=   '</div>';
    if (!done) {
      h += '<label class="rqd-seq-block__hold" style="display:flex;gap:6px;align-items:center;cursor:pointer;">'
        + '<input type="checkbox" data-abbyintro-optout="' + esc(ref) + '"' + (optOut ? ' checked' : '') + '>'
        + "<span>I'm handling the customer emails on this deal (no Abby intro)</span></label>";
    }
    h += '</div>';
    return h;
  }
  var _aiWired = false;
  function _wireAbbyIntroBlock() {
    if (_aiWired) return;
    _aiWired = true;
    document.addEventListener('change', function (ev) {
      var box = ev.target && ev.target.closest ? ev.target.closest('[data-abbyintro-optout]') : null;
      if (!box) return;
      var ref = box.getAttribute('data-abbyintro-optout');
      var want = !!box.checked;
      box.disabled = true;
      _cwPost('/quote-lifecycle', { ref: ref, abbyIntroOptOut: want }).then(function () {
        box.disabled = false;
        if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
      }).catch(function (e) {
        box.disabled = false; box.checked = !want;
        window.alert('Could not save it: ' + ((e && e.message) || e));
      });
    });
  }
  function _cwApi() { return (window.RepPortal && window.RepPortal.API) || null; }
  function _cwPost(path, body) {
    var api = _cwApi();
    if (!api || !api.fetch) return Promise.reject(new Error('not signed in'));
    return api.fetch(path, { method: 'POST', body: JSON.stringify(body) });
  }
  function _cwRepaint(ref, callWait) {
    var blk = document.querySelector('.rqd-seq-block--callwait[data-callwait-ref="' + String(ref || '').replace(/"/g, '\\"') + '"]');
    if (!blk) return false;
    var tmp = document.createElement('div');
    tmp.innerHTML = buildCallWaitBlock({ reference: ref, callWait: callWait });
    if (tmp.firstChild) blk.replaceWith(tmp.firstChild); else blk.remove();
    return true;
  }
  /* The call was logged and the worker woke the agent: say so in place (the feed line lands with
     the quiet refetch). Called by the note Save in dashboard-deal-feed.js. */
  function clearCallWaitBlock(ref, why) {
    var blk = document.querySelector('.rqd-seq-block--callwait[data-callwait-ref="' + String(ref || '').replace(/"/g, '\\"') + '"]');
    if (!blk) return false;
    blk.classList.add('rqd-seq-block--stopped');
    blk.innerHTML = '<div class="rqd-seq-block__head">'
      + '<span class="rqd-seq-block__icon" aria-hidden="true">✓</span>'
      + '<span class="rqd-seq-block__title">' + esc(why || 'Call logged. The AI agent was woken to continue.') + '</span>'
      + '</div>';
    return true;
  }
  var _cwWired = false;
  function _wireCallWaitBlock() {
    if (_cwWired) return;
    _cwWired = true;
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var parkBtn = t.closest('[data-callwait-park]');
      if (parkBtn) {
        ev.preventDefault();
        var f = document.querySelector('[data-callwait-form="' + parkBtn.getAttribute('data-callwait-park').replace(/"/g, '') + '"]');
        if (f) { f.hidden = !f.hidden; if (!f.hidden) { var r = f.querySelector('.rqd-callwait-form__reason'); if (r) r.focus(); } }
        return;
      }
      var cancelBtn = t.closest('[data-callwait-cancel]');
      if (cancelBtn) {
        ev.preventDefault();
        var f2 = cancelBtn.closest('.rqd-callwait-form');
        if (f2) f2.hidden = true;
        return;
      }
      var saveBtn = t.closest('[data-callwait-save]');
      if (saveBtn) {
        ev.preventDefault();
        var sref = saveBtn.getAttribute('data-callwait-save');
        var form = saveBtn.closest('.rqd-callwait-form');
        var reason = (form && form.querySelector('.rqd-callwait-form__reason') || {}).value || '';
        var lane = (form && form.querySelector('.rqd-callwait-form__lane') || {}).value || '';
        saveBtn.disabled = true; saveBtn.textContent = 'Parking…';
        _cwPost('/call-wait', { ref: sref, reason: reason.trim() || undefined, nextExpectedLane: lane || undefined }).then(function (res) {
          _cwRepaint(sref, (res && res.callWait) || { status: 'waiting', reason: reason, nextExpectedLane: lane });
          if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
        }).catch(function (e) {
          saveBtn.disabled = false; saveBtn.textContent = 'Park';
          window.alert('Could not park it: ' + ((e && e.message) || e));
        });
        return;
      }
      var clearBtn = t.closest('[data-callwait-clear]');
      if (clearBtn) {
        ev.preventDefault();
        var cref = clearBtn.getAttribute('data-callwait-clear');
        if (!window.confirm('Clear the wait on ' + cref + '?\n\nThe agent will NOT be woken by a logged call after this.')) return;
        clearBtn.disabled = true; clearBtn.textContent = 'Clearing…';
        _cwPost('/call-wait/clear', { ref: cref }).then(function () {
          _cwRepaint(cref, null);
          if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
        }).catch(function (e) {
          clearBtn.disabled = false; clearBtn.textContent = 'Clear';
          window.alert('Could not clear it: ' + ((e && e.message) || e));
        });
      }
    }, false);
  }
  /* END CUSTOM_CALLWAIT_BY_CLAUDE */

  /* "Thu 27 Aug, 2:15 pm" — the date AND the time, because Ryan asked for both and because
     "Thursday" alone leaves a rep wondering whether it beats the call they were about to make.
     ⚠️ Rendered in the READER's timezone, like every other timestamp on this feed. The worker
     schedules in Mountain; a rep in another zone should see their own clock, not ours. */
  function _seqWhenLabel(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var past = d.getTime() <= Date.now();
    var label;
    try {
      label = d.toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
      });
    } catch (_) { label = String(iso).slice(0, 16).replace('T', ' '); }
    /* ⛔ PAST-DUE READS "SENDING", NEVER A DATE THAT HAS BEEN AND GONE. The drain runs every
       fifteen minutes, so a few minutes either side of the hour is normal and a stale-looking
       date would read as a stuck job. */
    return past ? 'sending shortly' : label;
  }

  /* ============================================
     The block's two buttons
     ============================================
     ⛔ DELEGATED ON document, INSTALLED ONCE. The activity card is rebuilt from a string by a
     dozen call sites in main.js and none of them has a post-render hook — binding per render
     would mean finding all twelve and would leave the thirteenth silently dead. This listener
     outlives every repaint.
     ⚠️ Guarded by `_seqWired` rather than by trusting the script to load once: a second
     listener would double every fetch and, worse, double the Stop. */
  var _seqWired = false;
  function _wireSequenceBlock() {
    if (_seqWired) return;
    _seqWired = true;
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;

      var showBtn = t.closest('[data-seq-show]');
      if (showBtn) {
        ev.preventDefault();
        var sref = showBtn.getAttribute('data-seq-show');
        var body = document.querySelector('[data-seq-body="' + sref.replace(/"/g, '') + '"]');
        if (!body) return;
        if (!body.hidden) { body.hidden = true; showBtn.textContent = 'Show the copy'; return; }
        body.hidden = false;
        showBtn.textContent = 'Hide the copy';
        if (body.dataset.loaded === '1') return;
        body.innerHTML = '<div class="rqd-seq-block__loading">Loading…</div>';
        _seqFetch(sref).then(function (data) {
          body.dataset.loaded = '1';
          body.innerHTML = _seqRenderPending(data);
        }).catch(function (e) {
          /* ⛔ AN EMPTY PANEL WOULD READ AS "NOTHING IS QUEUED", which is the opposite of the
             truth and the exact confusion this block exists to remove. Not loaded is not empty. */
          body.innerHTML = '<div class="rqd-seq-block__loading">Could not load the copy ('
            + esc((e && e.message) || String(e)) + '). The emails are still queued.</div>';
        });
        return;
      }

      var stopBtn = t.closest('[data-seq-stop]');
      if (stopBtn) {
        ev.preventDefault();
        var rref = stopBtn.getAttribute('data-seq-stop');
        /* ⛔ CONFIRMED, BECAUSE IT CANNOT BE UNDONE FROM HERE. The stop writes a tombstone that
           refuses every future arm on this deal — that is what makes it a real off switch rather
           than a delete the next scan undoes — so pressing it by accident is expensive. */
        if (!window.confirm('Stop the automated emails on ' + rref + '?\n\n'
            + 'Nothing further will send on this deal automatically. This cannot be undone from here.')) return;
        stopBtn.disabled = true;
        stopBtn.textContent = 'Stopping…';
        _seqStop(rref).then(function (res) {
          /* ⛔ A 200 IS NOT A STOP (AIOPS 93691851c24f, Ryan 2026-09-10). `/sequence-stop` answers
             200 `{ok:false, noop:true, why}` when it found nothing to stop, and this painted the
             green ✓ on any resolved promise — so a stop that wrote nothing looked identical to one
             that wrote a tombstone, and the only tell was the banner coming back on refresh.
             The route no longer no-ops on the armed-and-waiting state, but the client must still
             say what actually happened rather than assume it. */
          if (res && res.ok === false) {
            stopBtn.disabled = false;
            stopBtn.textContent = 'Stop these';
            window.alert((res.why || 'Nothing automated is queued on this deal, so there was nothing to stop.')
              + String.fromCharCode(10,10) + 'Nothing was changed.');
            if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
            return;
          }
          var blk = stopBtn.closest('.rqd-seq-block');
          if (blk) {
            blk.classList.add('rqd-seq-block--stopped');
            blk.innerHTML = '<div class="rqd-seq-block__head">'
              + '<span class="rqd-seq-block__icon" aria-hidden="true">✓</span>'
              + '<span class="rqd-seq-block__title">Automated emails stopped. Nothing further will send on this deal.</span>'
              + '</div>';
          }
          /* The feed now carries a real entry recording who stopped it and when, so a refetch
             is what makes the block's disappearance and that entry arrive together. */
          if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
        }).catch(function (e) {
          stopBtn.disabled = false;
          stopBtn.textContent = 'Stop these';
          window.alert('Could not stop it: ' + ((e && e.message) || e));
        });
      }
    }, false);
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — THE BLOCK GOES WHEN THE THING IT ADVERTISES
     GOES, NOT ON THE NEXT REFRESH
     ============================================
     Ryan: *"I had a sample email automation going for RW-1363-A. I then got a call from the
     customer which I logged. The email automation email then didn't disappear until I refreshed
     the page."*

     ⛔ A CANCEL AND A STOP ARE THE SAME EVENT TO THIS BLOCK. Pressing 'Stop these' already
     rewrote it in place; a logged call cancels the very same job for the very same reason (the
     rep has spoken to the customer) and left the block claiming an email that no longer exists.
     One repainter, so the two paths cannot describe the outcome differently.

     📌 IT SAYS WHY. 'Automated emails stopped' after a Stop is self-explanatory; after a note
     it is not, so the verdict the worker sent travels into the sentence — a rep who did not
     realise their call would end the sequence should not have to infer it from an absence.
     ⚠️ DOM-ONLY, AND DELIBERATELY. The row's `pendingAutomation` is cleared by the caller and
     the quiet refetch behind it is the authoritative repaint; this is the half that lands
     inside one frame instead of inside three seconds. */
  function clearSequenceBlock(ref, why) {
    var blk = document.querySelector('.rqd-seq-block[data-seq-ref="' + String(ref || '').replace(/"/g, '\\"') + '"]');
    if (!blk) return false;
    blk.classList.add('rqd-seq-block--stopped');
    blk.innerHTML = '<div class="rqd-seq-block__head">'
      + '<span class="rqd-seq-block__icon" aria-hidden="true">✓</span>'
      + '<span class="rqd-seq-block__title">' + esc(why || 'Automated emails stopped. Nothing further will send on this deal.') + '</span>'
      + '</div>';
    return true;
  }

  function _seqApi() {
    return (window.RepPortal && window.RepPortal.API) || null;
  }
  function _seqFetch(ref) {
    var api = _seqApi();
    if (!api || !api.fetch) return Promise.reject(new Error('not signed in'));
    return api.fetch('/sequence-status?ref=' + encodeURIComponent(ref));
  }
  function _seqStop(ref) {
    var api = _seqApi();
    if (!api || !api.fetch) return Promise.reject(new Error('not signed in'));
    return api.fetch('/sequence-stop', { method: 'POST', body: JSON.stringify({ ref: ref }) });
  }

  /* ⛔ THE COPY IS RENDERED AS TEXT, ESCAPED. The worker returns `preview` as plain words for
     exactly this reason: the prose was rendered in an admin's browser and POSTed, so injecting
     it as markup here would be a stored-XSS hole opened for a preview pane. White-space is
     preserved by the CSS so the paragraphs still read as paragraphs. */
  function _seqRenderPending(data) {
    var seq = data && data.sequence;
    if (!seq || !seq.pending || !seq.pending.length) {
      /* Stopped and never-had-one are different answers, and saying "nothing queued" to a rep
         whose sequence somebody switched off last week is how the block loses their trust. */
      if (data && data.stopped) {
        return '<div class="rqd-seq-block__loading">Automated emails were stopped on this deal by '
          + esc(data.stopped.by || 'somebody') + '.</div>';
      }
      return '<div class="rqd-seq-block__loading">Nothing is queued on this deal.</div>';
    }
    var out = '';
    if (seq.repCode) {
      out += '<div class="rqd-seq-block__from">Sending from ' + esc(seq.repCode) + '’s mailbox, in this thread.</div>';
    }
    seq.pending.forEach(function (p) {
      out += '<div class="rqd-seq-block__rung">';
      out +=   '<div class="rqd-seq-block__rung-head">';
      out +=     '<span class="rqd-seq-block__rung-label">' + esc(p.label || p.id) + '</span>';
      /* ⚠️ ONLY THE NEXT RUNG HAS A REAL DATE. Every one after it is measured from the send
         BEFORE it, which has not happened — so it says "about a week later" rather than
         printing a date the schedule cannot keep. */
      out +=     '<span class="rqd-seq-block__rung-when">'
        + esc(p.sendAfter ? _seqWhenLabel(p.sendAfter)
              : (p.afterDays ? 'about ' + p.afterDays + ' days after that' : 'later in the sequence'))
        + '</span>';
      out +=   '</div>';
      if (p.subject) out += '<div class="rqd-seq-block__rung-subj">' + esc(p.subject) + '</div>';
      out +=   '<div class="rqd-seq-block__rung-copy">' + esc(p.preview || '(no copy)') + '</div>';
      if (p.note) out += '<div class="rqd-seq-block__rung-note">' + esc(p.note) + '</div>';
      out += '</div>';
    });
    return out;
  }

  function buildActivityCard(q, ctx) {
    ctx = ctx || {};
    var isPend = !!ctx.isPend;
    var html = '';

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Notes is now a
       structured activity log. Each save is its own entry with type
       chip + timestamp + author. Quick-action chips set the entry
       type (📞 Call, ✉ Email, 📅 Follow-up, ✅ Win, 💬 Note). Enter
       submits; Shift-Enter newline. Server-side: POST /note-entry-add
       appends to lead.noteEntries or wm.noteEntries or
       pending.noteEntries (whichever exists / is appropriate).
       Back-compat: legacy single-blob q.leadNotes (and now
       q.pendingNote) surfaces as one synthetic entry until a new entry
       gets saved. */
    var _noteEntries = Array.isArray(q.noteEntries) ? q.noteEntries.slice() : [];
    // Surface legacy blob as a synthetic entry when there are no
    // structured entries yet — preserves visibility of pre-feature
    // notes. Both q.leadNotes (lead/wm refs) and q.pendingNote
    // (pending refs) surface this way.
    /* CUSTOM_FEEDFIRST_BY_CLAUDE (Ryan 2026-10-10, RW-1379-C): NOT while the history is still on its
       way. The flat `leadNotes` blob is a plain-text copy of the feed the worker keeps for the
       Workstation; drawn here it showed raw timestamps and "Older notes" for 7-10 seconds, and it
       filled the empty branch below, so this deal's own feed was never asked for. */
    if (_noteEntries.length === 0 && !q._historyPending) {
      var _legacyText = (q.leadNotes && String(q.leadNotes).trim())
        ? String(q.leadNotes)
        : ((q.pendingNote && String(q.pendingNote).trim()) ? String(q.pendingNote) : '');
      if (_legacyText) {
        _noteEntries.push({
          id: '_legacy',
          ts: q.leadNotesUpdatedAt || null,
          by: q.leadNotesUpdatedBy || '',
          type: 'note',
          text: _legacyText,
          _legacy: true,
        });
      }
    }
    /* Newest first — except a PENDING email, which has not happened yet.
       CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29): *"the EMail held notification should
       appear higher up in the feed than the email sent one."*

       ⛔ NOT A TIEBREAK, AND A TIEBREAK WOULD NOT HAVE WORKED. Both rows read "8:49pm" but their
       timestamps are not equal — the hold is written first and /quote-mark-sent lands a moment
       later, so plain newest-first correctly puts the quote-sent row on top. Nudging a timestamp
       to beat it would be lying about when something happened.

       The honest rule: a held email is the one row in a log of PAST events that has not occurred
       yet, so it belongs ABOVE the log rather than inside it. Pinned only while its `sendAfter` is
       still in the future — once it sends it drops into the timeline on its own, with no state to
       clean up, and the drain's own "sent" entry takes over. */
    var _pendNow = Date.now();
    /* ⛔ THE KEY COMES FROM `_df_dedup` WHEN `heldMeta` IS ABSENT, AND THAT IS NOT A NICETY.
       `heldMeta` is written at QUEUE time, so it exists only on emails held from 2026-08-29
       onward — every hold already sitting in the queue has none, and gating on it shipped a
       feature that could not open the one email Ryan was looking at. `_df_dedup` has carried
       'held-email:' + the job key since the hold feature shipped, so this reaches all of them.

       ⚠️ "Still pending" is derived from the FEED, not from a clock. A legacy entry has no
       sendAfter to compare, and fetching per row is out of the question. The drain and the cancel
       route both write a terminal entry keyed on the same job — held-email-sent / -failed /
       -cancelled / -unknown — so the absence of one IS the pending signal, for old and new alike.
       Self-correcting: the moment it sends, its own entry appears and this unpins. */
    function _heldInfo(x, all) {
      var m = (x && x.heldMeta) || null;
      var key = (m && m.key) || null;
      if (!key && x && typeof x._df_dedup === 'string' && x._df_dedup.indexOf('held-email:') === 0) {
        key = x._df_dedup.slice('held-email:'.length);
      }
      if (!key) return null;
      var settled = (all || []).some(function (n) {
        var d = n && n._df_dedup;
        if (typeof d !== 'string') return false;
        if (!/^held-email-(sent|failed|cancelled|unknown):/.test(d)) return false;
        return d.slice(d.indexOf(':') + 1) === key;
      });
      return { key: key, meta: m, pending: !settled };
    }
    function _pendingRank(x) {
      var info = _heldInfo(x, _noteEntries);
      return (info && info.pending) ? 1 : 0;
    }
    _noteEntries.sort(function(a, b) {
      var pa = _pendingRank(a), pb = _pendingRank(b);
      if (pa !== pb) return pb - pa;
      var ta = a.ts ? new Date(a.ts).getTime() : 0;
      var tb = b.ts ? new Date(b.ts).getTime() : 0;
      return tb - ta;
    });
    /* CUSTOM_FEEDVIEW_BY_CLAUDE (Ryan 2026-10-10) — Rep view. What each entry IS is decided once,
       in dashboard-feed-view.js; this card only draws the answer. `_noteEntries` stays WHOLE:
       `_heldInfo` reads it to decide whether a held email has settled, and the drain's settling
       line is exactly the kind of system entry Rep view hides. The loop below walks `_feedEntries`.
       ⛔ Prospect cards (CFA outreach) keep every entry and get no switch: a different audience. */
    var _fv = (!ctx.isProspect && window.RQD && window.RQD.feedView) || null;
    /* CUSTOM_FEEDORIGIN_BY_CLAUDE (Ryan 2026-10-10) — THE DEAL'S STARTING POINT IS ALWAYS THE FIRST
       THING IN THE FEED. (1) A deal that started as a quote (+ New Quote / the Workstation), a website
       order, or a lead from before 2026-05-21 has no "lead came in" entry: one is BUILT here from the
       row (who, when, how), drawn only, never written (RW-1304-A/-B). (2) Whatever the timestamps
       say, the start-of-deal band is pinned to the bottom (oldest end) of the feed: a customer's
       email that reached a rep's inbox before the lead was filed was sorting ahead of it (JW-1309-B).
       ⚠️ Not while the history is still loading or only partly here (`_historyPartial`: the -A
       revision's real entry may be on its way with the board). */
    if (window.RQD && window.RQD.feedView && !ctx.isProspect && !q._historyPending) {
      var _originIdx = -1;
      for (var _oi = _noteEntries.length - 1; _oi >= 0; _oi--) {
        if (window.RQD.feedView.milestoneOf(_noteEntries[_oi]) === 'lead') { _originIdx = _oi; break; }
      }
      if (_originIdx === -1 && !q._historyPartial && (q.reference || '')) {
        var _oldestTs = q.createdAt || '';
        for (var _oj = 0; _oj < _noteEntries.length; _oj++) {
          var _ts = _noteEntries[_oj] && _noteEntries[_oj].ts;
          if (_ts && (!_oldestTs || String(_ts) < String(_oldestTs))) _oldestTs = _ts;
        }
        var _who = String(q.rep || q.assignee || '').toUpperCase();
        var _how = q.source === 'online'
          ? 'Website order placed'
          : (q.createdFromLead ? 'Lead created' : 'Quote created' + (_who ? ' by ' + _who : ''));
        var _ls = String(q.leadSource || '').toLowerCase();
        if (q.source !== 'online' && (_ls === 'inbound' || _ls === 'outbound' || _ls === 'shared')) _how += ' · ' + _ls;
        _noteEntries.push({ id: '_synth_origin', _synthOrigin: true, ts: _oldestTs || null, by: '_SYSTEM', type: 'lead-intake', types: ['lead-intake'], text: _how + '.' });
        _originIdx = _noteEntries.length - 1;
      }
      if (_originIdx !== -1 && _originIdx !== _noteEntries.length - 1) {
        _noteEntries.push(_noteEntries.splice(_originIdx, 1)[0]);
      }
    }
    var _feedClass = function (x) {
      if (!_fv) return { kind: 'activity', show: true, agent: false };
      var c = _fv.classify(x);
      /* Ryan 2026-10-10 (RW-1486-A): a HELD email shows in Rep view only while it is still waiting.
         Once it has sent, the email itself is on the feed and the "held for business hours" line
         is a record of the wait, so it goes to Everything. Settled-or-not is the card's own
         `_heldInfo`, which reads the WHOLE list (the drain's "sent" line is hidden in Rep view). */
      if (c.show && c.kind === 'email' && !x.emailMeta) {
        var _hi = _heldInfo(x, _noteEntries);
        if (_hi && !_hi.pending) return { kind: 'system', show: false, agent: false };
      }
      return c;
    };
    var _feedEntries = (_fv && _fv.isRepView())
      ? _noteEntries.filter(function (x) { return _feedClass(x).show; })
      : _noteEntries;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Notes card v2.
       Desktop 3-column layout: 5-button type stack LEFT (Note / Call
       / Email / Meeting / Drop-In), 3-line textarea MIDDLE, inline
       follow-up cluster RIGHT (preset durations + method chips),
       Save button bottom-right (replaces Enter-submit). Default-
       armed follow-up per lifecycle: NEW LEAD/SAMPLE=+3d, PENDING=
       +14d, QUOTED/'...'=+7d, SOLD=OFF (longer-cadence preset shelf
       for SOLD: +14/+30/+60/+90/+180). Mobile (<760px) stacks
       vertically. */
    var _chipDefs = [
      { type: 'note',    emoji: '💬', label: 'Note' },
      { type: 'call',    emoji: '📞', label: 'Call' },
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-07) — Text (SMS) activity,
         logged like any other note type. Sits right after Call. */
      { type: 'text',    emoji: '📱', label: 'Text' },
      { type: 'email',   emoji: '📧', label: 'Email' },
      { type: 'meeting', emoji: '🤝', label: 'Meeting' },
      { type: 'dropin',  emoji: '🚪', label: 'Drop-In' },
    ];
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21, globalized 2026-07-12) —
       Hide the manual "email" note chip for reps who have Outlook CONNECTED.
       Auto-pulled emails land on the deal feed automatically; allowing manual
       "email" notes alongside that would create duplicates + signal the wrong
       workflow. Gated on the connected flag fetched at module load (was a
       hardcoded rep allowlist). Unconnected reps keep the chip as their fallback. */
    if (_outlookConnected) {
      _chipDefs = _chipDefs.filter(function(c) { return c.type !== 'email'; });
    }
    // Decide which lifecycle default applies. null = no pre-arm.
    var _isSoldLike = (q.lifecycleStatus === 'sold' || q.status === 'sold');
    var _isLostLike = (q.lifecycleStatus === 'lost' || q.status === 'lost');
    var _isLeadLike = (q.isLead || q.isSample || q.status === 'new_lead' || q.status === 'sample');
    var _isSample = (q.isSample || q.status === 'sample');
    var _isOnHold = (q.isOnHold || q.status === 'on_hold');
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Saving a note
       with a follow-up date on ANY lead-state row (lead:{ref} record
       exists) triggers an auto-hold server-side. Save button label
       flips to "Put on Hold" when those two conditions are met so
       the rep sees what's about to happen. NEW LEAD / SAMPLE / DEAD
       / ON HOLD all match. PENDING and wm-state rows (QUOTED, '...',
       SOLD, LOST, EXPIRED) keep "Save" since saves there don't
       change status. */
    var _isLeadStateRow = _isLeadLike || _isOnHold || q.isDeadLead || q.status === 'dead_lead';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — RESUMED leads
       (wokenFromHold flag set, still in new_lead status) escape the
       short-shelf treatment. The lead has already been through one
       hold cycle, so the rep has earned the longer-cadence preset
       shelf (+7/+14/+30/+60/+90/+180) — same as QUOTED/SOLD. SAMPLE
       leads don't carry the wokenFromHold flag through resurrection
       the same way; treat them as fresh. */
    var _isResumed = !!(q.wokenFromHold && (q.status === 'new_lead' || q.isLead) && !q.isSample);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Preset shelf
       length policy:
         - NEW LEAD / SAMPLE: short shelf (+1/+2/+3/+4/+5) — leads
           go cold fast, these are chase-cadence.
         - ON HOLD with active timer: short shelf — until the timer
           expires, the rep is editing within the same hold window.
         - RESUMED, ON HOLD with expired timer, everything else:
           longer shelf.
       The date picker (calendar) is now ALWAYS rendered. The short
       preset shelf is a nudge to chase, not a hard cap — when a
       customer explicitly says "I'm thinking 8 months out", the rep
       needs to capture that. Anti-gaming concern moves to backend
       reporting (avg time NEW LEAD → QUOTED per rep), not frontend
       UI restriction. */
    var _holdExpired = false;
    if (_isOnHold && q.holdUntil) {
      // Ryan 2026-05-16: matches the universal 4am-ET anchor used
      // by formatHoldCountdown / isFollowUpExpired / autoWakeIfDue
      // (FU_DAY_ANCHOR = 'T08:00:00Z'). Was T23:59:59 local which
      // never triggered on the same date the rep saved the hold.
      var _holdEnd = new Date(String(q.holdUntil) + 'T08:00:00Z');
      _holdExpired = !isNaN(_holdEnd.getTime()) && _holdEnd <= new Date();
    }
    var _useShortShelf = (_isLeadLike && !_isResumed) || (_isOnHold && !_holdExpired);
    var _hasExistingFollowUp = !!((q.followUp && q.followUp.dueAt) || q.holdUntil
      || _isOnHold);
    var _defaultDays = null;
    if (!_hasExistingFollowUp && !q.isDeadLead && !_isLostLike && !_isSoldLike && !q.cancelled) {
      if (q.isPending || q.status === 'pending') _defaultDays = 14;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-07) — Sample requests
         pre-arm a +7d follow-up. Samples take ~4-5 days to ship/deliver,
         so chasing before the customer has even received them is
         premature. Must precede the _isLeadLike branch below (a sample
         is also lead-like). */
      else if (_isSample) _defaultDays = 7;
      /* Ryan 2026-05-16: NEW LEAD / RESUMED no longer pre-arm a follow-
         up. The rep picks one (preset or calendar) if they want one, or
         saves a note alone — being intentional about scheduling matters
         more than a "default chase" nudge. Previously: +2d (NEW LEAD) /
         +7d (RESUMED). (SAMPLE now pre-arms +7d — see above.) */
      else if (_isLeadLike || _isResumed) _defaultDays = null;
      else _defaultDays = 7; // QUOTED / '...' / under_review / submitted
    }
    var _presetDays;
    if (_isSoldLike) _presetDays = [7, 14, 30, 60, 90, 180];
    else if (_isResumed) _presetDays = [7, 14, 30, 60, 90, 180];
    /* Ryan 2026-05-16: LOST gets the longest cadence shelf — these
       are speculative re-engagement timers ("check back in 6 months
       when they're refreshing the gym"), not chase-cadence. +365d
       covers the "annual budget cycle" case. */
    else if (_isLostLike) _presetDays = [14, 30, 60, 90, 180, 365];
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-07) — Sample requests get a
       6th preset, +7d, defaulted (see _defaultDays). Samples ship/deliver
       in ~4-5 days; +7d is the first sensible chase. Must precede the
       generic short-shelf branch (a sample is also short-shelf). */
    else if (_isSample) _presetDays = [1, 2, 3, 4, 5, 7];
    else if (_useShortShelf) _presetDays = [1, 2, 3, 4, 5];
    else _presetDays = [3, 7, 14, 30, 90, 180];
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — the Prospecting tab reuses this
       composer via ctx.isProspect. Prospects carry no offline lifecycle/hold state,
       so pin the follow-up shelf to the prospect cadence + no pre-arm (preserves the
       prior prospect-card behavior). */
    if (ctx.isProspect) { _presetDays = [1, 3, 7, 14, 30, 90]; _defaultDays = null; }
    var _armedYmd = _defaultDays ? daysFromNowYmd(_defaultDays) : '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Notes card is
       collapsible like the rest of the workflow cards. Reuses the
       existing .rqd-detail__arap--collapsible / --collapsed class
       machinery + .rqd-arap__chevron glyph so the click router at
       line ~1510 picks it up for free. State persists across re-
       renders via collapsedSections[ref]['notes']. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — On PENDING rows
       the Activity card is non-collapsible. PENDING detail panels
       render with only two cards (Sold To + Activity), so collapsing
       Activity would leave the rep staring at a half-empty panel.
       Once the row transitions out of pending (QUOTED / SOLD / ON
       HOLD / etc.), the card reverts to the normal collapsible
       treatment — there's a full panel of other cards to manage at
       that point. Implementation: skip the --collapsible class and
       the chevron on pending rows; collapse state read still
       defers to collapsedSections for non-pending. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Online orders
       default the Activity card to COLLAPSED unless entries exist.
       99% of online orders never get a rep-typed note (they're real
       Shopify checkouts that flow through fulfillment without a
       sales conversation), so leaving the card auto-expanded just
       pushes the rest of the detail panel down for no payoff. Show
       it collapsed initially; rep clicks the chevron to expand if
       they want to leave a note. Other states (offline / lead /
       etc.) keep the existing collapsedSections-based default. */
    /* ⚠️ `&& !q._historyPending` for the same reason as the empty state below: a cache-painted row
       has no `noteEntries` yet, and treating that as "this online order has no activity" would fire
       whatever this flag drives on a deal whose history simply has not arrived. */
    var _onlineEmptyActivity = (q.source === 'online') && !q._historyPending
      && (!Array.isArray(q.noteEntries) || q.noteEntries.length === 0);
    var _notesCollapsed = !isPend && (
      _onlineEmptyActivity || !!(collapsedSections[q.reference] && collapsedSections[q.reference].notes)
    );
    var _notesCollapsibleCls = isPend ? '' : ' rqd-detail__arap--collapsible';
    var _notesCollapsedCls = _notesCollapsed ? ' rqd-detail__arap--collapsed' : '';
    html += '<div class="rqd-detail__card rqd-notes-log' + _notesCollapsibleCls + _notesCollapsedCls + '" data-ref="' + esc(q.reference) + '" data-default-days="' + (_defaultDays || '') + '" data-section="notes" style="margin-top:14px;">';
    /* Ryan 2026-05-16: Card title renamed "Notes" → "Activity" now
       that the log shows both rep-typed notes AND system-generated
       state transitions. Internal data-section identifier stays
       "notes" so existing collapse state in collapsedSections[ref]
       keeps working across sessions. CSS classes also unchanged
       (.rqd-notes-log__* throughout). Count badge surfaces total
       entries in the activity log so the rep can scan "how much
       is on this deal" at a glance. Hidden when zero entries (the
       empty-state copy already says "No activity yet."). */
    var _entryCount = _noteEntries.filter(function (x) { return !(x && x._synthOrigin); }).length; // CUSTOM_FEEDORIGIN_BY_CLAUDE — the built band is not an entry
    var _countBadge = _entryCount > 0
      ? ' <span class="rqd-notes-log__count">(' + _entryCount + ')</span>'
      : '';
    var _chevronHtml = isPend ? '' : '<span class="rqd-arap__chevron">' + (_notesCollapsed ? '+' : '−') + '</span>';
    html += '<div class="rqd-detail__section-title">Activity' + _countBadge + _chevronHtml + '</div>';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21, revised) — Composer at
       top (chips + textarea + follow-up cluster with Save inside —
       IDENTICAL to the original layout). Below, a NEW two-column split:
       left 2/3 = DEAL FEED (entries list with subheading), right 1/3 =
       LINKED FILES. Right edges of FOLLOW UP (top right) + LINKED FILES
       (bottom right) align so the column visually flows top-to-bottom. */
    html += '<div class="rqd-notes-log__composer">';
    html += '<div class="rqd-notes-log__layout">';
    // LEFT — vertical type-chip stack. For Outlook-enabled reps, the
    // "📨 New Email" action button is injected at position 2 (between
    // Note and Call) — chip-shaped but functionally an action button
    // (opens the compose modal). Distinct class so the chip click
    // handler ignores it.
    var _qCustEmail = ctx.isProspect
      ? (ctx.custEmail || (q.customer && q.customer.email) || '')
      : (q.customerEmail || q.email
        || (q.shipTo && q.shipTo.email)
        || (q.billTo && q.billTo.email)
        || (q.customer && q.customer.email)
        || (q.lead && (q.lead.customerEmail || q.lead.email))
        || '');
    var _showNewEmailBtn = _outlookConnected;
    html += '<div class="rqd-notes-log__types rqd-notes-log__chips" role="group" aria-label="Note type">';
    _chipDefs.forEach(function(c, idx) {
      var active = c.type === 'note' ? ' rqd-notes-log__chip--active' : '';
      html += '<button type="button" class="rqd-notes-log__chip' + active + '" data-note-type="' + esc(c.type) + '" aria-pressed="' + (c.type === 'note' ? 'true' : 'false') + '">' + c.emoji + ' <span class="rqd-notes-log__chip-label">' + esc(c.label) + '</span></button>';
      // After Note (index 0), inject the New Email action button for Outlook reps.
      if (idx === 0 && _showNewEmailBtn) {
        /* Carry both classes so the button inherits the full chip
           styling (including per-status overrides like
           .rqd-detail--quoted .rqd-notes-log__chip blue border).
           No data-note-type → chip click handler bails (gated on
           dataset.noteType existing) → new-email-btn handler
           downstream catches it. */
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — the pill now offers reply-vs-new,
           the choice the QW's Send Quote has had since 2026-05-30 and the DD never did. Ryan:
           *"when you're in the DD and you click email however, it doesn't give you this choice..
           It should."*

           ⛔ THE MENU IS EMPTY UNTIL A THREAD IS KNOWN TO EXIST. Whether this customer has a
           thread is a server question (/deal-email-thread reads BOTH the lead and workstation
           feeds), so it cannot be answered at render time. The pill's handler asks on click and
           only then opens this; with no thread it goes straight to a fresh email and the menu is
           never shown — per Ryan, a chooser with one real option is just a click in the way.

           Markup is the `Contact again` drop-down from the freight card, verbatim
           (.rqd-fqc__menu-wrap / __menu / __menu-item) — Ryan asked for that look, and reusing
           the classes means the dark-mode and z-index rules come with it. Those rules are global,
           not scoped under a freight card, and dashboard.css already declares
           `.rqd-fqc__menu[hidden]{display:none}` BEFORE its `display:flex`, so the hidden
           attribute actually wins here. */
        html += '<div class="rqd-fqc__menu-wrap">'
              + '<button type="button" class="rqd-notes-log__chip rqd-notes-log__new-email-btn" '
              +   'data-ref="' + esc(q.reference || '') + '" '
              +   'data-to="' + esc(_qCustEmail) + '" '
              +   'aria-haspopup="true" aria-expanded="false" '
              +   'title="Email this customer via Outlook">'
              +   '✉ <span class="rqd-notes-log__chip-label">Email</span>'
              + '</button>'
              + '<div class="rqd-fqc__menu rqd-notes-log__email-menu" hidden>'
              +   '<button type="button" class="rqd-fqc__menu-item" data-email-mode="reply">↳ Reply on existing thread</button>'
              +   '<button type="button" class="rqd-fqc__menu-item" data-email-mode="new">New email</button>'
              + '</div>'
              + '</div>';
      }
    });
    // Call "Focus mode" (Ryan 2026-05-29): the outcome selector lives INSIDE the
    // type column so it stacks directly beneath the Call chip. Collapsed via CSS
    // until .rqd-notes-log__types gets .is-calling (set when Call is active),
    // at which point the other type chips collapse and Call slides to the top.
    // Drives the cadence: No answer = chase/advance, Connected/Inbound = reset.
    html += '<div class="rqd-notes-log__calloutcome" data-oc-stage="root">';
    html +=   '<div class="rqd-notes-log__oc-crumbs"><span class="rqd-notes-log__oc-crumb-label">Outcome</span></div>';
    html +=   '<div class="rqd-notes-log__oc-step" data-oc-step="root">';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="inbound">Inbound</button>';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="outbound">Outbound</button>';
    html +=   '</div>';
    html +=   '<div class="rqd-notes-log__oc-step" data-oc-step="outbound" hidden>';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="connected">Connected</button>';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="no-answer">No answer</button>';
    html +=   '</div>';
    html +=   '<div class="rqd-notes-log__oc-step" data-oc-step="no-answer" hidden>';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="left-vm">Left VM</button>';
    html +=     '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip" data-oc-go="no-vm">Didn&rsquo;t leave VM</button>';
    html +=   '</div>';
    /* CUSTOM_CALLDETAIL_BY_CLAUDE (Ryan 2026-09-20) — WHY there was no voicemail. OPTIONAL, and
       it is the only step in this tree that is: "Didn't leave VM" is already a terminal leaf, so
       by the time these appear the outcome is stamped, the note is prefilled and Save is live.
       Ignore them and save exactly as before; pick one and it adds a sentence to the note plus a
       `callDetail` on the entry.
       ⛔ NOT a `data-oc-go` node — these carry `data-oc-detail` so the decision-tree handler in
       dashboard-main.js cannot mistake one for an outcome and re-stamp callOutcome.
       Labels come from RQD.CALL_DETAILS (dashboard-deal-feed.js, which loads first) so the chip,
       the note sentence and the feed line cannot drift apart.
       ⚠️ NO HINT LINE ABOVE THE CHIPS (Ryan 2026-09-20, removed the day it shipped). The crumb
       already reads "Didn't leave VM" directly above them and the four labels are self-evident
       under it, so a "Why? (optional)" caption was a line of chrome explaining a line of chrome.
       Dropping it also takes the step back UNDER the composer's 180px row lock — see the CSS. */
    var _cdList = (window.RQD && window.RQD.CALL_DETAILS) || [];
    if (_cdList.length) {
      html += '<div class="rqd-notes-log__oc-step rqd-notes-log__oc-step--detail" data-oc-step="no-vm-detail" hidden>';
      _cdList.forEach(function(d) {
        html += '<button type="button" class="rqd-notes-log__chip rqd-notes-log__calloutcome-chip rqd-notes-log__oc-detail-chip" data-oc-detail="' + esc(d.id) + '">' + esc(d.chip) + '</button>';
      });
      html += '</div>';
    }
    html += '</div>';
    html += '</div>';
    // MIDDLE — textarea (3 lines tall), now full-width (the call-outcome
    // selector moved into the left type column above).
    html += '<div class="rqd-notes-log__input-col">';
    html += '<textarea class="rqd-notes-log__input" data-ref="' + esc(q.reference) + '" rows="3" placeholder="Type a note — click Save when ready"></textarea>';
    html += '</div>';
    // RIGHT — follow-up cluster (always visible, no popover).
    html += '<div class="rqd-notes-log__followup" data-ref="' + esc(q.reference) + '"' + (_armedYmd ? ' data-armed-date="' + esc(_armedYmd) + '"' : '') + '>';
    html +=   '<div class="rqd-notes-log__followup-header">';
    html +=     '<span class="rqd-notes-log__followup-title">Follow up</span>';
    html +=     '<span class="rqd-notes-log__followup-armed-date"' + (_armedYmd ? '' : ' hidden') + '>' + (_armedYmd ? '— ' + esc(formatFollowUpDate(_armedYmd)) : '') + '</span>';
    html +=   '</div>';
    var _currentFuDate = (q.followUp && q.followUp.dueAt) || (q.holdUntil) || '';
    if (_currentFuDate) {
      /* Ryan 2026-05-21: dropped the "(Xd Yh remaining)" suffix.
         Date alone is enough; the countdown lives elsewhere in
         the dashboard (status pills + follow-up column) so it
         was duplicate info that bloated this row's height. */
      html +=   '<div class="rqd-notes-log__followup-current" title="Existing follow-up — Save without touching the presets/date to keep it as-is">Current: ' + esc(formatFollowUpDate(_currentFuDate)) + '</div>';
    }
    html +=   '<div class="rqd-notes-log__followup-presets">';
    _presetDays.forEach(function(d) {
      var activeCls = (_defaultDays === d) ? ' rqd-notes-log__followup-pick--active' : '';
      html += '<button type="button" class="rqd-notes-log__followup-pick' + activeCls + '" data-days="' + d + '" title="In ' + d + ' days">+' + d + 'd</button>';
    });
    html +=   '</div>';
    /* Ryan 2026-05-21: date input + Save button moved to a side-by-
       side flex row (50/50). Was stacked vertically — the extra
       row of height pushed the follow-up cluster taller than the
       shortened textarea on its left. Side-by-side layout tightens
       the cluster to match the new shorter activity textarea. */
    html += '<div class="rqd-notes-log__followup-actions">';
    html +=   '<div class="rqd-notes-log__followup-custom-wrap' + (_armedYmd ? ' rqd-notes-log__followup-custom-wrap--has-value' : '') + '">';
    html +=     '<input type="date" class="rqd-notes-log__followup-custom" value="' + esc(_armedYmd) + '" aria-label="Custom follow-up date">';
    html +=     '<span class="rqd-notes-log__followup-custom-placeholder" aria-hidden="true">mm/dd/yyyy</span>';
    html +=   '</div>';
    var _holdActionLabel = _isOnHold ? 'Edit Hold Date' : 'Put on Hold';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-07) — Inline composer Save
       button now ALWAYS reads "Save". The lead-state hold-label flip
       ("Put on Hold" / "Edit Hold Date") overflowed off the button (sloppy
       UX). The auto-hold still happens server-side when a follow-up is
       armed on a lead row — we just no longer relabel the button. Setting
       holdable='' makes both runtime label-swappers (main.js
       _syncSaveButtonLabel + dashboard-deal-feed.js finally) fall through
       to "Save". The separate "Put on Hold" cog-menu modal is unaffected. */
    var _saveLabel = 'Save';
    var _holdable = '';
    html +=   '<button type="button" class="rqd-notes-log__submit" disabled data-ref="' + esc(q.reference) + '" data-save-label="Save" data-hold-label="' + esc(_holdActionLabel) + '" data-holdable="' + _holdable + '" title="Save note (type a note first)">' + esc(_saveLabel) + '</button>';
    html += '</div>';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — "Save & Mark Lost/Dead":
       logs the activity AND closes the deal out in one save, for when the rep is
       giving up after repeated no-response follow-ups (leave the last voicemail,
       record it, mark lost). Stage-aware (quoted -> Lost, lead -> Dead); hidden on
       already-resolved deals. Starts disabled, enabled once a note (the reason) is
       typed; the worker's closeOut flag bypasses the follow-up requirement and the
       client fires the canonical /quote-lifecycle (lost) or /lead-mark-dead. */
    var _coIsQuoteStage = !!(q.lifecycleStatus || q.chainQuoted || q.pdfDownloaded || q.chainPdfDownloaded);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — prospects mark LOST (not "dead");
       handleNoteCloseOut routes their close-out to /cfa-prospect-archive. A prospect
       already closed (won/lost/handoff) hides the close-out (ctx.isClosed). */
    var _coMode = ctx.isProspect ? 'lost' : (_coIsQuoteStage ? 'lost' : 'dead');
    var _coResolved = _isLostLike || _isSoldLike || q.isDeadLead || q.status === 'dead_lead' || q.closedStatus === 'closed' || q.cancelled || (ctx.isProspect && ctx.isClosed);
    if (!_coResolved) {
      var _coWord = _coMode === 'lost' ? 'Lost' : 'Dead';
      html += '<button type="button" class="rqd-notes-log__closeout" disabled data-ref="' + esc(q.reference) + '" data-closeout-mode="' + _coMode + '" title="Log this note and mark the deal ' + _coWord + ' — type the reason first">Save &amp; Mark ' + _coWord + '</button>';
    }
    html += '</div>'; // .rqd-notes-log__followup
    html += '</div>'; // .rqd-notes-log__layout
    html += '</div>'; // .rqd-notes-log__composer

    // Bottom row: DEAL FEED (left 2/3) + LINKED FILES (right 1/3). Open
    // the split + feed; the existing entries loop renders inside the
    // feed; sidecol + linked-files are appended after entries close.
    html += '<div class="rqd-notes-log__split' + (ctx.isProspect ? ' rqd-notes-log__split--no-sidecol' : '') + '">';
    html += '<div class="rqd-notes-log__feed">';
    html += '<div class="rqd-notes-log__sidecol-title rqd-notes-log__sidecol-title--feed">DEAL FEED'
      + (_fv ? _fv.toggleHtml() : '') // CUSTOM_FEEDVIEW_BY_CLAUDE — Rep view / Everything
      + '</div>';
    // Entries list
    html += '<div class="rqd-notes-log__entries">';
    /* ⛔ ABOVE THE ENTRIES, NOT AMONG THEM. The feed is a record of what HAS happened, ordered
       newest-first; a pending email has not happened and would be filed under a date it has not
       reached. Ryan asked for it "frozen at the top" for exactly that reason. */
    html += buildSequenceBlock(q);
    html += buildCallWaitBlock(q); // CUSTOM_CALLWAIT_BY_CLAUDE — parked until the customer's call (state + the admin's Park / Clear)
    html += buildFollowUpBlock(q); // CUSTOM_FOLLOWUPS_BY_CLAUDE (AIOPS deadff7115ea) — who we are waiting on, until they answer
    html += buildAbbyIntroBlock(q); _wireAbbyIntroBlock(); // CUSTOM_ABBYINTRO_BY_CLAUDE (AIOPS 67db43d9ee42) — the intro's state + the rep's "I'm handling the emails"
    if (_noteEntries.length === 0) {
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-03) — ⛔ AN ABSENT HISTORY IS NOT AN EMPTY
         ONE, AND MUST NOT BE ANNOUNCED AS ONE. The sessionStorage copy of the deal list drops
         `noteEntries` so the board can be cached at all (see `_slimForCache` in dashboard-main.js);
         a row painted from that copy carries `_historyPending` and NO array. Without this branch a
         deal with fifteen emails on it would read "No activity yet. Add the first note above." for
         the moment before the background refresh lands — a confident false statement, and an
         invitation to write a note the deal did not need.
         ⚠️ The refresh is already in flight when this renders; it is not a fetch this card has to
         start, which is why the copy says loading rather than offering a retry. */
      /* CUSTOM_DDFEEDSTUCK_BY_CLAUDE (AIOPS 847f234e793b, Ryan 2026-09-14) — ⛔ AND IT HAS TO BE
         ADDRESSABLE, BECAUSE IT COULD HANG HERE FOREVER. The note above is right that this card
         does not start the fetch — and that is exactly the bug Ryan reported: *"expanding a deal
         sometimes shows Loading activity... and it never loads."* The repaint this was waiting for
         is gated on `hasUnsavedTyping()`, and expanding a deal is what PUTS a composer on screen;
         click into it and every quiet repaint is blocked, so the history never arrives. The
         revalidate can also have finished, or failed, before the row was ever expanded, in which
         case nothing is in flight at all and the sentence above is simply not true.
         ⚠️ THE REF AND THE START TIME RIDE ON THE ELEMENT so dashboard-main.js can find it: it
         pushes the history in when the repaint is skipped (`_repaintEntriesFor`), and a watchdog
         turns a spinner nobody is feeding into an error with a Retry (`_watchStuckHistory`). The
         copy still says loading rather than offering a retry immediately, which is right — a
         retry offered at t=0 reads as though something already went wrong. */
      html += q._historyPending
        ? '<div class="rqd-notes-log__empty rqd-notes-log__empty--loading" data-history-pending="' + esc(q.reference || '') + '" data-since="' + Date.now() + '">Loading activity&hellip;</div>'
        : '<div class="rqd-notes-log__empty">No activity yet. Add the first note above.</div>';
      /* CUSTOM_FEEDFIRST_BY_CLAUDE (Ryan 2026-10-10) — ask for THIS deal's feed now rather than wait
         for the whole board (dashboard-main.js _fetchHistoryFor; one request per deal per page). */
      if (q._historyPending && q.reference && window.RQD && typeof window.RQD.fetchHistoryFor === 'function') {
        var _fhRef = q.reference;
        setTimeout(function () { try { window.RQD.fetchHistoryFor(_fhRef); } catch (_e) { /* the watchdog retries */ } }, 0);
      }
    } else {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Delete button.
         Admin: always allowed. Rep: allowed on their OWN entries
         within 30 min of creation only (protects historical context
         from quiet edits). Server-side enforces the same gates;
         client-side eligibility just decides whether to render the
         button. Legacy synthetic entries (id === '_legacy') get no
         delete affordance — they're not a real entry server-side. */
      var _sessCode = ((window.RepPortal && window.RepPortal.Auth.getSession() || {}).code || '').toUpperCase();
      var _NOTE_DELETE_GRACE_MS = 30 * 60 * 1000;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Render every
         type on an entry. e.types is the canonical multi-value
         field; e.type stays as the legacy singular for back-compat.
         Fall back to the legacy types ('followup', 'win') so old
         entries display correctly during the rollout. */
      /* Ryan 2026-05-21: workflow-event chip mappings. Cover the full
         deal lifecycle from intake → quote → ordering → install →
         closed. Worker handlers write entries with these types;
         the chip render pulls the right emoji + label automatically. */
      var _legacyEmojiMap = {
        call: '📞', text: '📱', email: '✉', meeting: '🤝', dropin: '🚪', note: '💬',
        followup: '📅', win: '✅',
        'sample-shipped': '📦',
        // Freight / install
        'freight-req': '🚛', 'install-req': '🛠️',
        'freight-cost': '🚛', 'install-cost': '🛠️',
        // Lifecycle / workflow
        'lead-intake': '🌱',
        'reassign': '🔀',
        'quote-pdf': '📄',
        'quote-sent': '📨',
        'revision': '🔄',
        'po-received': '📃',
        'payment': '💰',
        'vendor-po': '🏭',
        'vendor-purchased': '🛒',
        'vendor-web-purchased': '🌐',
        'vendor-po-void': '🚫',
        'vendor-shipped': '🚚',
        'sample-request': '🧪',
        'customer-edit': '✏️',
        'invoice-sent': '🧾',
        'install-sched': '🗓️',
        'install-done': '✅',
        'deal-closed': '🏁',
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — automated follow-up sequences. ⛔ ITS
           OWN TYPE, NOT 'note'. The samples ladder has been writing its sends as plain notes
           since 2026-08-19, which buries "we emailed your customer without you" among a rep's
           own typing. A sequence arming, sending, holding, failing or stopping are all the same
           KIND of event and a rep scanning a feed should be able to pick them out. */
        'automation': '⚡',
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — PHASE 2: one customer, one deal. ⛔ A
           NEW FEED TYPE NEEDS A ROW IN BOTH MAPS OR IT DRAWS AS A GENERIC 💬 Note — and these
           two are exactly the events a rep scans a feed to find: "was this a duplicate?" and
           "is this the same customer as that other row?". */
        'submission-folded': '🔁',
        'submission-joined': '🔗',
      };
      var _legacyLabelMap = {
        call: 'Call', text: 'Text', email: 'Email', meeting: 'Meeting', dropin: 'Drop-In', note: 'Note',
        followup: 'Follow-up', win: 'Win',
        'sample-shipped': 'Sample Shipped',
        'freight-req': 'Freight Requested', 'install-req': 'Install Requested',
        'freight-cost': 'Freight Quoted', 'install-cost': 'Install Quoted',
        'lead-intake': 'Lead Created',
        'reassign': 'Reassigned',
        'quote-pdf': 'Quote PDF',
        'quote-sent': 'Quote Sent',
        'revision': 'Revision',
        'po-received': 'PO Received',
        'payment': 'Payment',
        'vendor-po': 'Vendor PO',
        'vendor-purchased': 'Direct Purchase',
        'vendor-web-purchased': 'Web Purchase',
        'vendor-po-void': 'PO Voided',
        'vendor-shipped': 'Shipped',
        'sample-request': 'Sample Requested',
        'customer-edit': 'Customer Info Updated',
        'invoice-sent': 'Invoice',
        'install-sched': 'Install Scheduled',
        'install-done': 'Install Complete',
        'deal-closed': 'Deal Closed',
        /* One label for arm / send / hold / fail / stop — the SENTENCE says which, and five
           near-identical chips would be noise where one is a filter. */
        'automation': 'Automation',
        /* The labels for the two Phase-2 types above. "Duplicate Absorbed" is the submission
           that never became a deal; "Same Customer" is one that did and was grouped with this. */
        'submission-folded': 'Duplicate Absorbed',
        'submission-joined': 'Same Customer',
      };
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — System-
         generated transition entries (type='system' + transition
         block) render with a pill-pair header — (OLD STATUS) → (NEW
         STATUS) — instead of the normal type-chip + label. Maps
         worker status keys to the dashboard's existing status-pill
         CSS classes so the pills use the same colors as the row's
         Status column. */
      var _statusToPillClass = {
        'on_hold': 'hold',
        'new_lead': 'awaiting',
        'sample': 'sample',
        'dead_lead': 'dead',
        'sold': 'sold',
        'lost': 'lost',
        'quoted': 'quoted',
      };
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — prospect stage transitions
         (cold/engaged/qualified/handoff/won/lost) aren't in the quote/lead status map
         above, so on a prospect card they fell back to gray 'awaiting' on every full
         re-render. Map them to the prospect-stage pill classes
         (rqd-notes-log__transition-pill--prospect-{stage}) so they keep their color. */
      var _PROSPECT_STAGE_KEYS = ['cold', 'engaged', 'qualified', 'handoff', 'won', 'lost'];
      function _pillClassFor(status) {
        if (ctx.isProspect && _PROSPECT_STAGE_KEYS.indexOf(status) !== -1) return 'prospect-' + status;
        return _statusToPillClass[status] || 'awaiting';
      }
      /* Ryan 2026-05-21: pagination for long deal feeds. Default-
         visible cap is generous (50) so most deals render fully
         without a toggle; longer threads collapse the older
         entries behind a "Show N older entries" button. Entries
         are already sorted newest-first upstream so the visible
         slice is always the freshest. We render ALL entries inline
         (no client-side state sync needed) — entries past index 50
         are wrapped in a hidden `<div>` that the button toggles. */
      var DEAL_FEED_DEFAULT_CAP = 50;
      var _totalEntries = _feedEntries.length; // CUSTOM_FEEDVIEW_BY_CLAUDE — the cap counts what this view shows
      var _hiddenCount = Math.max(0, _totalEntries - DEAL_FEED_DEFAULT_CAP);
      var _hiddenOpen = false; // track whether we've injected the open <div>
      _feedEntries.forEach(function(e, _idx) {
        var _fc = _feedClass(e); // CUSTOM_FEEDVIEW_BY_CLAUDE — milestone band / agent 🤖
        var _ms = (_fv && _fc.milestone) ? _fv.milestone(_fc.milestone) : null;
        if (_idx === DEAL_FEED_DEFAULT_CAP) {
          // Inject the "Show N more" button + open the hidden wrapper.
          html += '<button type="button" class="rqd-notes-log__show-more" data-ref="' + esc(q.reference) + '">Show ' + _hiddenCount + ' older ' + (_hiddenCount === 1 ? 'entry' : 'entries') + '</button>';
          html += '<div class="rqd-notes-log__hidden-entries" hidden>';
          _hiddenOpen = true;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-14) — the floating "from
           RW-1129-A" origin badge was REMOVED: it floated in an inconsistent
           position and was redundant — the entry header already names the source
           revision (e.g. "Revision RW-1115-E created from RW-1115-B by JW").
           originRef still drives the email/edit-entry routing under the hood
           (see _mutRef / entryRef below); only the visible badge is gone. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — PHASE 3: AND IT COMES BACK FOR ONE CASE,
           WHICH IS THE CASE THE 06-14 REASONING DOES NOT COVER.
           ─────────────────────────────────────────────────────────────────────────────────────
           That badge was redundant because within a REVISION CHAIN the entry header already names
           the source revision. Nothing names a source DEAL. Since Phase 2 a repeat submission from
           the same customer auto-groups with the deal its owner already holds, the worker cascades
           the sibling's CONVERSATION onto this feed (`applyGroupFeedCascade`), and the client draws
           a whole group as ONE cascade — so a call logged on JW-1284-A sits in JW-1282-A's feed
           with nothing whatsoever saying so.

           ⛔ A MERGED FEED THAT CANNOT SAY WHICH DEAL A LINE CAME FROM IS WORSE THAN TWO FEEDS.
           ⛔ SO IT KEYS ON `originGroup`, NOT ON `originRef`. Every cascaded REVISION entry carries
           originRef too; keying on that would reinstate exactly the badge Ryan removed, on exactly
           the entries he removed it from. A revision origin stays silent.
           ⚠️ INLINE, NOT FLOATING — the other half of the 06-14 complaint. It rides in the meta
           line beside "(edited)" on a note and among the badges on an email row, both of which are
           ordinary inline flow. */
        var _originBadge = '';
        if (e.originGroup && e.originRef) {
          _originBadge = '<span class="rqd-notes-log__entry-origin" title="Logged on ' + esc(e.originRef)
            + ' — a separate deal grouped with this one for the same customer.">from ' + esc(e.originRef) + '</span>';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        var isSystem = (e.type === 'system' || (Array.isArray(e.types) && e.types.indexOf('system') !== -1)) && e.transition;
        if (isSystem) {
          var fromCls = _pillClassFor(e.transition.from);
          var toCls = _pillClassFor(e.transition.to);
          var sysByLabel = _byLabelFor(e.by) || 'System';
          html += '<div class="rqd-notes-log__entry rqd-notes-log__entry--system' + (_ms ? ' rqd-feed-milestone rqd-feed-milestone--' + _ms.tone : '') + '" data-entry-id="' + esc(e.id || '') + '">';
          html += '<div class="rqd-notes-log__entry-meta">';
          if (_ms) html += '<span class="rqd-notes-log__entry-type rqd-feed-milestone__badge"><span class="rqd-feed-milestone__icon">' + _ms.icon + '</span>' + esc(_ms.label) + '</span>'; // CUSTOM_FEEDVIEW_BY_CLAUDE
          html += '<span class="rqd-notes-log__entry-transition">';
          html += '<span class="rqd-notes-log__transition-pill rqd-notes-log__transition-pill--' + esc(fromCls) + '">' + esc(e.transition.fromLabel || e.transition.from) + '</span>';
          html += '<span class="rqd-notes-log__transition-arrow">&rarr;</span>';
          html += '<span class="rqd-notes-log__transition-pill rqd-notes-log__transition-pill--' + esc(toCls) + '">' + esc(e.transition.toLabel || e.transition.to) + '</span>';
          html += '</span>';
          html += '<span class="rqd-notes-log__entry-meta-sep"> — </span>';
          html += '<span class="rqd-notes-log__entry-when">' + whenStackHtml(e.ts, sysByLabel, false) + '</span>';
          html += _originBadge;
          /* Ryan 2026-05-16: empty mutate-btns span so the desktop
             right-align CSS (margin-left: auto + order: 1 on
             mutate-btns, order: 2 on when) absorbs the free space
             and pushes the timestamp flush right. System entries
             are never rep-mutable so the span stays empty —
             zero-width but still triggers the flex margin auto. */
          html += '<span class="rqd-notes-log__entry-mutate-btns"></span>';
          html += '</div>';
          /* Ryan 2026-05-16: skip the body text on auto-triggered
             transitions when it's the stock "Timer expired." string.
             Worker stopped writing that string per the 2026-05-16
             autoWakeIfDue fix, but legacy KV entries from before
             that deploy still carry it. Filter at render time so
             they read cleanly without a redundant italic line. */
          if (e.text && e.text !== 'Timer expired.') html += '<div class="rqd-notes-log__entry-text rqd-notes-log__entry-text--system">' + esc(e.text).replace(/\n/g, '<br>') + '</div>';
          html += '</div>';
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — Outlook
           email entry: rich render path that surfaces direction,
           subject (bold), from/to line, body preview, attachment
           + CC indicators, and a thread-size badge. Click expands
           the full body in a modal. Bypasses the generic chip-
           based meta row entirely. */
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — A HELD EMAIL RENDERS LIKE A SENT ONE.
           Ryan: *"make the entry a little taller with a preview of the email and make it clickable
           ... clicking it brings up the window where you can click Reply and Close, but instead you
           should be able to click Edit and Cancel."*

           It reuses the `.rqd-email-entry` markup wholesale — same rows, same preview, same 📎 —
           so a pending email reads as an email rather than as a log line about one. The only
           differences are a PENDING pill carrying the send time and the `--held` modifier.

           ⛔ IT MUST COME BEFORE THE SENT-EMAIL BRANCH. That branch is `e.emailMeta && type ===
           'email'`; a held entry is type 'automation' and carries `heldMeta`, so the two cannot
           collide today — but ordering them this way means a future entry carrying both cannot
           silently fall through to the wrong renderer.
           ⚠️ Everything here comes off `heldMeta`, which the worker writes onto the feed entry at
           queue time. No fetch: the row must paint with the board, like every other row. */
        /* ⛔ `.pending` GATES THE RENDER, NOT JUST THE SORT. This read `if (_hInfo)`, so ANY entry
           carrying a job key drew the held row — including one whose email had already sent or
           been cancelled. The result was a settled entry still wearing a PENDING pill, still
           pinned to the top, and still clickable, which then answered "this email has already sent
           or was cancelled" when opened. Ryan hit precisely that after a re-send: the superseded
           Monday row sat above the live Sunday one, both looking equally live, and the composer's
           "sends tomorrow" appeared to contradict the feed.
           A settled hold is HISTORY: it falls through to the ordinary automation chip, which is
           what it is — a log line saying an email was once held. `pending` was already computed
           for the sort; using it here too is what makes the two agree. */
        var _hInfo = _heldInfo(e, _noteEntries);
        if (_hInfo && _hInfo.pending) {
          /* ⚠️ TWO GRADES OF ROW, ON PURPOSE. With `heldMeta` (queued 2026-08-29+) the row shows
             the real recipients, the real preview and the real attachment names. Without it — an
             email already in the queue when this shipped — everything the feed knows is the
             sentence the hold wrote, so the subject is lifted out of that and the sentence itself
             becomes the preview. Both are clickable, and the MODAL fetches the truth either way,
             so a legacy row is thinner but never wrong. */
          var hm = _hInfo.meta || {};
          var _hLegacyText = String(e.text || '');
          var _hSubjMatch = _hLegacyText.match(/Subject:\s*[“"]([\s\S]*?)[”"]\s*$/);
          var hAtts = Array.isArray(hm.attachments) ? hm.attachments : [];
          var hTo = Array.isArray(hm.to) ? hm.to : [];
          var hCc = Array.isArray(hm.cc) ? hm.cc : [];
          var hSendsLabel = hm.sendAfter ? formatNoteDate(hm.sendAfter) : '';
          if (!hm.key) {
            hm = {
              key: _hInfo.key,
              subject: (_hSubjMatch && _hSubjMatch[1]) || '(held email)',
              repCode: e.by || '',
              bodyPreview: _hLegacyText,
            };
            hAtts = []; hTo = []; hCc = []; hSendsLabel = '';
          }
          /* The paperclip carries the COUNT and the names in its tooltip — Ryan's "I want to be
             able to see on this if the attachment is on this email too" is answered on the row,
             without opening anything. */
          var hAttachBadge = hAtts.length
            ? '<span class="rqd-email-entry__attach" title="' + esc(hAtts.map(function (a) { return a.name; }).join(', ')) + '">📎'
              + (hAtts.length > 1 ? ' ' + hAtts.length : '') + '</span>'
            : '';
          html += '<div class="rqd-notes-log__entry rqd-email-entry rqd-email-entry--outbound rqd-email-entry--held"'
            + ' role="button" tabindex="0"'
            + ' data-held-key="' + esc(hm.key) + '" data-entry-id="' + esc(e.id || '') + '"'
            + ' aria-label="Open the held email' + (hSendsLabel ? ' — sends ' + esc(hSendsLabel) : '') + '">';
          html += '<div class="rqd-email-entry__row1">';
          html +=   '<span class="rqd-email-entry__icon" aria-hidden="true">⌛</span>';
          html +=   '<span class="rqd-email-entry__subject-wrap"><span class="rqd-email-entry__subject-text">'
                  + esc(hm.subject || '(no subject)') + '</span></span>';
          html +=   '<span class="rqd-email-entry__held-pill" title="This email has not sent yet">PENDING</span>';
          html +=   hAttachBadge;
          html +=   '<span class="rqd-email-entry__when">' + whenStackHtml(e.ts, esc(hm.repCode || ''), e._legacy, 'rep') + '</span>';
          html += '</div>';
          /* Every part of this row is conditional — a legacy entry knows neither the recipients
             nor the send time, and "To:" followed by nothing reads as a broken row rather than an
             unknown one. Print what is known; say nothing about what is not. */
          var _hRow2 = '';
          if (hTo.length) {
            _hRow2 += 'To: ' + esc(hTo[0])
              + (hTo.length > 1 ? ' <span class="rqd-email-entry__more">+' + (hTo.length - 1) + '</span>' : '');
          }
          if (hSendsLabel) {
            _hRow2 += (_hRow2 ? ' ' : '')
              + '<span class="rqd-email-entry__held-when">' + (hTo.length ? '· ' : '')
              + 'sends ' + esc(hSendsLabel) + '</span>';
          }
          if (_hRow2) html += '<div class="rqd-email-entry__row2">' + _hRow2 + '</div>';
          if (hCc.length) {
            html += '<div class="rqd-email-entry__cc">cc: ' + esc(hCc.slice(0, 3).join(', '))
                  + (hCc.length > 3 ? ' +' + (hCc.length - 3) : '') + '</div>';
          }
          html += '<div class="rqd-email-entry__preview">' + esc(hm.bodyPreview || '') + '</div>';
          html += '</div>';
          return;
        }
        if (e.emailMeta && e.type === 'email') {
          var em = e.emailMeta;
          var isInbound = em.direction === 'inbound';
          /* Inline SVG arrows instead of emojis — system emoji rendering
             (📥/📤) was too small + the tray base obscured the arrow
             direction. SVGs use currentColor so CSS controls hue per
             status, are crisp at any size, and the arrow shape alone
             carries the direction signal. */
          /* Curved arrows — Bezier-arc shafts + crisp arrowhead:
             - Inbound:  starts top-left, arcs RIGHT then DOWN,
               lands at bottom-middle with tip pointing straight
               down. "Came in from out there, lands here."
             - Outbound: starts middle-left, arcs UP then RIGHT,
               exits at the top-right corner with tip pointing
               right. "Leaves here, goes out there." (v2 of this
               path — Ryan flagged 2026-05-21 that the prior shape
               bunched up at the tip; this version has a longer,
               smoother arc.)
             Hue contrast (blue inbound vs amber outbound) +
             arc-shape mirror = instantly distinguishable. */
          var dirIcon = isInbound
            ? '<svg class="rqd-email-arrow rqd-email-arrow--inbound" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3 C 8 3, 8 8, 8 13 M5 10 L 8 13 L 11 10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
            : '<svg class="rqd-email-arrow rqd-email-arrow--outbound" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8 C 3 4, 8 4, 12 4 M9 1 L 12 4 L 9 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
          var subj = em.subject || '(no subject)';
          // Inline meta (the generic path defines var meta = …, but our
          // branch returns before reaching it).
          var emailMetaDate = e._legacy ? 'Older notes' : formatNoteDate(e.ts);
          // Sender / recipient line
          var addrLine;
          if (isInbound) {
            var fromDisplay = em.fromName
              ? esc(em.fromName) + ' &lt;' + esc(em.from || '') + '&gt;'
              : esc(em.from || '');
            addrLine = 'From: ' + fromDisplay;
          } else {
            var toArr = Array.isArray(em.to) ? em.to : [];
            var toNamesArr = Array.isArray(em.toNames) ? em.toNames : [];
            var toFirst = toArr[0] || '';
            var toFirstDisplay = toNamesArr[0]
              ? esc(toNamesArr[0]) + ' &lt;' + esc(toFirst) + '&gt;'
              : esc(toFirst);
            addrLine = 'To: ' + toFirstDisplay + (toArr.length > 1 ? ' <span class="rqd-email-entry__more">+' + (toArr.length - 1) + '</span>' : '');
          }
          // CC line (only if non-empty)
          var ccArr = Array.isArray(em.cc) ? em.cc : [];
          var ccLine = ccArr.length
            ? '<div class="rqd-email-entry__cc">cc: ' + esc(ccArr.slice(0, 3).join(', ')) + (ccArr.length > 3 ? ' +' + (ccArr.length - 3) : '') + '</div>'
            : '';
          // Attachment indicator
          var attachBadge = em.hasAttachments ? '<span class="rqd-email-entry__attach" title="Has attachment">📎</span>' : '';
          var mailFilterBadge = _mailFilterTag(em); // CUSTOM_MAILFILTERTAG_BY_CLAUDE
          // Thread badge — only if the wider thread has more than 1 email on this deal
          var threadCount = 0;
          if (em.conversationId) {
            threadCount = (q.noteEntries || []).filter(function(n) {
              return n && n.emailMeta && n.emailMeta.conversationId === em.conversationId;
            }).length;
          }
          var threadBadge = threadCount > 1
            ? '<span class="rqd-email-entry__thread" title="' + threadCount + ' emails in this thread on this deal">thread &middot; ' + threadCount + '</span>'
            : '';
          // Read/unread (only meaningful for inbound; outbound is always read)
          var readClass = (isInbound && !em.isRead) ? ' rqd-email-entry--unread' : '';
          /* Ryan 2026-05-21 (rev): internal-sender / internal-recipient
             detection. ANY email where the non-rep participant is also
             @blueskyfitsupply.com (notifications@, shipping@, another
             rep) is "internal" — separates system / coworker comms
             from customer-facing comms. For inbound, check the from
             address. For outbound, check the TO list; if every
             recipient is internal, tag it. */
          var INTERNAL_DOMAIN_RX = /@blueskyfitsupply\.com$/i;
          var _isInternal = false;
          if (isInbound) {
            _isInternal = !!(em.from && INTERNAL_DOMAIN_RX.test(em.from));
          } else {
            // Outbound — every recipient must be internal for the tag to fire
            var _toArr = Array.isArray(em.to) ? em.to : [];
            _isInternal = _toArr.length > 0 && _toArr.every(function(addr) { return addr && INTERNAL_DOMAIN_RX.test(addr); });
          }
          var internalClass = _isInternal ? ' rqd-email-entry--internal' : '';
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-13) — sender label for the
             email's timestamp stack: ABBY for the orders@ system mailbox,
             otherwise the sender's initials (from the display name, falling
             back to the deal's rep prefix on outbound). Customer-inbound stays
             blank — the "From:" row already names them. A sender label makes
             the internal nature obvious, so the redundant "internal" badge is
             dropped whenever a label is shown (also fixes Abby's customer email
             being mislabeled internal just because orders@ is an @bsf address). */
          var senderLabel = '';
          var senderKind = ''; // 'customer' → amber name (matches the outbound arrow); else gray
          var _fromLc = (em.from || '').toLowerCase();
          if (_fromLc === 'orders@blueskyfitsupply.com') {
            senderLabel = 'ABBY';
          } else if (!isInbound) {
            var _nm = (em.fromName || '').trim();
            if (_nm) {
              var _p = _nm.split(/\s+/);
              senderLabel = (_p[0].charAt(0) + (_p.length > 1 ? _p[_p.length - 1].charAt(0) : '')).toUpperCase();
            }
            if (!senderLabel) senderLabel = String(q.reference || '').split('-')[0].toUpperCase();
          } else if (!_isInternal) {
            /* Inbound from an external participant — the main contact OR anyone
               else on the thread (principal, coach, PE teacher…); they're all
               "our customer". Show their FIRST name (CSS uppercases it). */
            var _inm = (em.fromName || '').trim();
            senderLabel = _inm ? _inm.split(/\s+/)[0] : (em.from || '').split('@')[0].split(/[._+]/)[0];
            senderKind = 'customer';
          }
          var internalBadge = (_isInternal && !senderLabel) ? '<span class="rqd-email-entry__internal" title="Internal email — both sides are @blueskyfitsupply.com">internal</span>' : '';
          var preview = em.bodyPreview || (e.text || '').replace(/^[→ ]+/, '').replace(new RegExp('^' + (subj || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*—\\s*'), '');
          /* Defense-in-depth: clean the preview at render time so
             already-synced entries display nicely without forcing a
             re-pull. The preview is just the first ~280 chars — no
             anchor tags, no rich rendering. So we drop ALL angle-
             bracket-wrapped URLs (the actual links live in the
             modal where they get reconstructed as clickable
             anchors). */
          preview = String(preview || '')
            .replace(/\[(?:cid|signature|image|picture)[_:][^\]]+\]/gi, '')
            .replace(/<https?:\/\/[^>]+>/g, '')
            .replace(/<mailto:[^>]+>/gi, '')
            .replace(/([A-Za-z0-9\)\].,!?:;"'])(https?:\/\/)/g, '$1 $2')
            .replace(/\s+\n/g, '\n')
            .trim();
          // Edit/delete buttons are NEVER shown on auto-pulled
          // emails (system-stamped — see _isSystemStamped at L8961
          // which now includes _OUTLOOK).
          /* Ryan 2026-05-21 (revision): rows are now DIRECT children
             of .rqd-email-entry so the parent's
             `> :not(:first-child) { padding-left: 48px }` rule
             naturally indents rows 2-4 (From/To, CC, preview),
             matching the hanging-indent pattern other entries use.
             Row 1 (icon + subject + when) stays at the entry's
             natural left edge — matches the chip-row on notes. The
             whole entry is the click target (role="button" on the
             outer div). */
          html += '<div class="rqd-notes-log__entry rqd-email-entry rqd-email-entry--' + (isInbound ? 'inbound' : 'outbound') + readClass + internalClass + '" role="button" tabindex="0" data-entry-id="' + esc(e.id || '') + '" data-conv="' + esc(em.conversationId || '') + '" data-msg="' + esc(em.messageId || '') + '" aria-label="Open full email">';
          html += '<div class="rqd-email-entry__row1">';
          html +=   '<span class="rqd-email-entry__icon" aria-hidden="true">' + dirIcon + '</span>';
          html +=   '<span class="rqd-email-entry__subject-wrap"><span class="rqd-email-entry__subject-text">' + esc(subj) + '</span></span>';
          html +=   attachBadge;
          html +=   mailFilterBadge;
          html +=   threadBadge;
          html +=   internalBadge;
          html +=   _originBadge;
          html +=   '<span class="rqd-email-entry__when">' + whenStackHtml(e.ts, esc(senderLabel), e._legacy, senderKind) + '</span>';
          html += '</div>';
          html += '<div class="rqd-email-entry__row2">' + addrLine + '</div>';
          html += ccLine;
          html += '<div class="rqd-email-entry__preview">' + esc(preview || '').replace(/\n/g, '<br>') + '</div>';
          html += '</div>';
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Combined
           entry: rep's note ALSO carries a transition (set when
           their save triggered an auto-hold server-side). Render
           the normal type chip + label header AND the pill-pair
           on a second line so one entry tells the full story:
           rep's chip + text + the state change it caused. Avoids
           double-entries with identical timestamps. */
        var hasTransition = !!e.transition;
        var typesArr = Array.isArray(e.types) && e.types.length > 0
          ? e.types
          : (e.type ? [e.type] : ['note']);
        var emojiStr = typesArr.map(function(t) { return _legacyEmojiMap[t] || _legacyEmojiMap.note; }).join('');
        var labelStr = typesArr.map(function(t) { return _legacyLabelMap[t] || 'Note'; }).join(' · ');
        /* CUSTOM_FEEDVIEW_BY_CLAUDE (Ryan 2026-10-10) — a milestone wears its own icon and words,
           and an agent's entry leads with 🤖 instead of the type emoji, so a rep scrolling can tell
           a colleague's (or their own) note from a bot's at a glance. The label stays ("🤖 NOTE"). */
        var _chipCls = 'rqd-notes-log__entry-type';
        if (_ms) {
          emojiStr = '<span class="rqd-feed-milestone__icon">' + _ms.icon + '</span>';
          labelStr = _ms.label;
          _chipCls += ' rqd-feed-milestone__badge';
        } else if (_fc.agent) {
          emojiStr = '🤖';
        } else if (_fv && !e._legacy && !_fv.isTyped(e) && emojiStr.indexOf('💬') !== -1) {
          /* Ryan 2026-10-10: 💬 is reserved for a note a person wrote in their own words. A line the
             worker recorded because somebody did something ("Freight requested by RW ...") is a
             ledger entry: 📋 LOGGED. */
          emojiStr = emojiStr.replace(/💬/g, '📋');
          labelStr = labelStr.replace(/\bNote\b/g, 'Logged');
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Translate
           '_SYSTEM' / '_AUTO' byline tokens to "System" / "Auto" so
           sample-shipped + future auto-logged entries don't render the
           raw underscore sentinel. Mirrors the transition-entry path's
           sysByLabel logic at L8656. */
        var _byLabel = _byLabelFor(e.by);
        /* CUSTOM_NOTEDELETE_BY_CLAUDE (AIOPS 842fead8b4da, Ryan 2026-09-10) — AN AGENT'S DELETE IS
           SOFT, AND THIS IS WHERE AN ADMIN SEES IT.
           ⛔ ONLY AN ADMIN EVER RECEIVES A TOMBSTONED ENTRY. lib/deal-feed.js drops it from the
           payload for everybody else — reps AND the agent that removed it — so this branch is not
           a second gate and must not be read as one. If `e.deletedAt` is here at all, the worker
           already decided this viewer may see it; the job of this code is only to make it LOOK
           removed, so an admin scanning a feed does not read a struck-out note as live. */
        var _isDeleted = !!(e && e.deletedAt);
        var meta = whenStackHtml(e.ts, _byLabel, e._legacy)
          + (e.editedAt ? '<span class="rqd-notes-log__entry-edited" title="Edited ' + esc(formatNoteDate(e.editedAt)) + (e.editedBy ? ' by ' + esc(e.editedBy) : '') + '">(edited)</span>' : '')
          + (_isDeleted ? '<span class="rqd-notes-log__entry-edited rqd-notes-log__entry-removed" title="Removed ' + esc(formatNoteDate(e.deletedAt)) + (e.deletedBy ? ' by ' + esc(e.deletedBy) : '') + '. Only admins can see this.">(removed' + (e.deletedBy ? ' by ' + esc(e.deletedBy) : '') + ')</span>' : '')
          + _originBadge;
        // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Edit + delete
        // share the same gate: admin always; rep only on own entries
        // within 30 min. Pencil ✎ for edit, × for delete.
        var canMutate = false;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — System-stamped
           entries (sample shipments, future auto-logged events) carry
           `by: '_SYSTEM'` and must not expose edit/delete UI. The
           worker's /note-entry-delete handler already rejects them on
           by-guard, but rendering the buttons would invite confusing
           failures. Mirror the worker rule client-side. */
        /* Ryan 2026-05-21: _OUTLOOK added — auto-pulled emails from
           the Outlook integration are read-only just like sample-
           shipment entries. Editing the entry text wouldn't change
           the email in the user's mailbox, and deleting it would
           just get re-linked by the next cron pass. */
        var _isSystemStamped = e.by === '_SYSTEM' || e.by === '_AUTO' || e.by === '_OUTLOOK';
        if (!e._legacy && e.id && !_isSystemStamped) {
          if (getIsAdmin()) {
            canMutate = true;
          } else if (e.by && String(e.by).toUpperCase() === _sessCode && e.ts) {
            var _ageMs = Date.now() - new Date(e.ts).getTime();
            if (isFinite(_ageMs) && _ageMs <= _NOTE_DELETE_GRACE_MS) canMutate = true;
          }
        }
        /* Ryan 2026-05-16: combined entries (note + transition —
           i.e., the rep's note ALSO triggered a status change) get
           ONLY the edit button, not delete. Deleting would orphan
           the state change from its rationale in the activity log.
           Edit lets the rep correct their text without losing the
           transition record. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — mutations target the
           record the entry actually LIVES on (originRef for cascaded
           entries). Previously a cascaded entry's edit/delete posted the
           displayed row's ref and the worker 404'd ("Note entry not found"). */
        var _mutRef = e.originRef || q.reference;
        /* CUSTOM_NOTEDELETE_BY_CLAUDE (AIOPS 842fead8b4da) — ⛔ NO EDIT AND NO DELETE ON A ROW THAT
           IS ALREADY REMOVED. Both buttons post the entry id to the worker, which would find the
           tombstoned entry and act on it: deleting it again does nothing visible and reads as a
           broken button, and EDITING it would rewrite a note nobody can see. */
        if (_isDeleted) canMutate = false;
        var mutateBtns = canMutate
          ? '<button type="button" class="rqd-notes-log__entry-edit-btn" data-ref="' + esc(_mutRef) + '" data-entry-id="' + esc(e.id) + '" title="Edit this note" aria-label="Edit note">&#9998;</button>'
            + (hasTransition ? '' : '<button type="button" class="rqd-notes-log__entry-delete" data-ref="' + esc(_mutRef) + '" data-entry-id="' + esc(e.id) + '" title="Delete this note" aria-label="Delete note">&#10005;</button>')
          : '';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        /* CUSTOM_NOTEDELETE_BY_CLAUDE (AIOPS 842fead8b4da) — the strike-through is a STATE CLASS on
           the row, not a style on the text span, so the chip, the timestamp and the byline dim with
           it and the row reads as one removed thing rather than a live entry with odd text. */
        html += '<div class="rqd-notes-log__entry' + (_isDeleted ? ' rqd-notes-log__entry--removed' : '')
          + (_ms ? ' rqd-feed-milestone rqd-feed-milestone--' + _ms.tone : '') // CUSTOM_FEEDVIEW_BY_CLAUDE
          + '" data-entry-id="' + esc(e.id || '') + '">';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Combined-entry
           row order: the STATE CHANGE leads (pills + timestamp + by +
           edit btn on row 1), then the type chip "💬 NOTE" sits below
           (row 2), then the note body (row 3). Reads top-down as
           "the change happened → here's what kind of activity it was
           → here's the rep's note". When there's no transition,
           fall back to the original chip+when single-row meta. */
        /* Ryan 2026-05-16: chip + note text now flow INLINE on the
           same row instead of being stacked (chip above, text on
           row below). Reads as "💬 NOTE — Call Mike tomorrow"
           with the chip prefixed to the note content like a tag.
           On desktop the timestamp + mutate buttons sit
           right-aligned at the far right of the same row; on
           mobile they trail naturally at the end. Long notes
           wrap within the text flex item without disturbing the
           right-side metadata. */
        var _entryTextHtml = '<span class="rqd-notes-log__entry-text">' + esc(_repairNoteText(e.text)).replace(/\n/g, '<br>') + '</span>';
        /* CUSTOM_FEEDFORMAT_BY_CLAUDE (Ryan 2026-09-12) — ⛔ THE CALL ENTRY WAS FOUR DIFFERENT
           TREATMENTS STACKED. Ryan, reading one on RW-1380-B: *"everything is a different
           weight/size/font/colour/pill vs no pill"* — a 10px amber pill for the outcome, 13px text
           for the note, an 11px grey line for the follow-up, and the pills on a row of their own
           above the chip. It is now ONE header and three labelled lines in one font.

           The outcome stops being a pill and becomes a sentence, because it reads as one:
           ⚠️ "no answer" is DROPPED when a voicemail was left — "Outbound · No answer · Left VM"
           says the same thing twice, since you cannot leave a voicemail on a call somebody
           answered. Inbound carries no outcome at all: nobody logs a missed inbound call. */
        var _callDetails = '';
        if (e && e.callOutcome) {
          var _ocO = String(e.callOutcome);
          if (_ocO === 'inbound') _callDetails = 'Inbound';
          else if (_ocO === 'connected') _callDetails = 'Outbound, connected';
          else if (e.callVm === 'left') _callDetails = 'Outbound, left voicemail';
          else _callDetails = 'Outbound, no answer';
          /* CUSTOM_CALLDETAIL_BY_CLAUDE (Ryan 2026-09-20) — the optional reason, as the tail of
             the same sentence: "Outbound, no answer — mailbox full". Same helper as the
             optimistic renderer in dashboard-deal-feed.js, so the line cannot read one way the
             instant you save and another after the next refresh. */
          if (window.RQD && typeof window.RQD.callDetailTail === 'function') {
            _callDetails += window.RQD.callDetailTail(e);
          }
        }
        var _isCall = !!_callDetails;
        /* The pill pair, built once so the CALL header can carry it inline after the chip
           ("📞 CALL - (SAMPLE) → (ON HOLD)") and the plain transition row can use it as its own
           header. It used to exist only inside the transition branch, which is why a call that
           also moved the status put its pills on one row and its chip on the next. */
        var _transitionHtml = '';
        if (hasTransition) {
          var _toClsT = _pillClassFor(e.transition.to);
          _transitionHtml += '<span class="rqd-notes-log__entry-transition">';
          if (e.transition.from === e.transition.to) {
            _transitionHtml += '<span class="rqd-notes-log__transition-pill rqd-notes-log__transition-pill--' + esc(_toClsT) + '">' + esc(e.transition.toLabel || e.transition.to) + '</span>';
            if (e.transition.qualifier) _transitionHtml += '<span class="rqd-notes-log__transition-qualifier">' + esc(e.transition.qualifier) + '</span>';
          } else {
            var _fromClsT = _pillClassFor(e.transition.from);
            _transitionHtml += '<span class="rqd-notes-log__transition-pill rqd-notes-log__transition-pill--' + esc(_fromClsT) + '">' + esc(e.transition.fromLabel || e.transition.from) + '</span>';
            _transitionHtml += '<span class="rqd-notes-log__transition-arrow">&rarr;</span>';
            _transitionHtml += '<span class="rqd-notes-log__transition-pill rqd-notes-log__transition-pill--' + esc(_toClsT) + '">' + esc(e.transition.toLabel || e.transition.to) + '</span>';
          }
          _transitionHtml += '</span>';
        }
        if (_isCall) {
          /* ONE header row: the chip, then the pills inline when the call also moved the status,
             then the timestamp stack and the edit buttons where every other entry keeps them. */
          html += '<div class="rqd-notes-log__entry-meta">'
            + '<span class="' + _chipCls + '">' + emojiStr + ' ' + esc(labelStr) + '</span>'
            + (_transitionHtml ? '<span class="rqd-notes-log__entry-meta-dash"> - </span>' + _transitionHtml : '')
            + '<span class="rqd-notes-log__entry-when">' + meta + '</span>'
            + '<span class="rqd-notes-log__entry-mutate-btns">' + mutateBtns + '</span>'
            + '</div>';
          html += '<div class="rqd-notes-log__entry-line"><span class="rqd-notes-log__entry-line-label">Call details:</span> ' + esc(_callDetails) + '</div>';
          if (e.text && String(e.text).trim()) {
            html += '<div class="rqd-notes-log__entry-line"><span class="rqd-notes-log__entry-line-label">Call notes:</span> '
              + esc(_repairNoteText(e.text)).replace(/\n/g, '<br>') + '</div>';
          }
        } else if (hasTransition) {
          html += '<div class="rqd-notes-log__entry-meta">';
          if (_ms) html += '<span class="' + _chipCls + '">' + emojiStr + ' ' + esc(labelStr) + '</span>'; // CUSTOM_FEEDVIEW_BY_CLAUDE — a sold / lost note keeps its band
          html += _transitionHtml;
          html += '<span class="rqd-notes-log__entry-meta-sep"> — </span>';
          html += '<span class="rqd-notes-log__entry-when">' + meta + '</span>';
          html += '<span class="rqd-notes-log__entry-mutate-btns">' + mutateBtns + '</span>';
          html += '</div>';
          /* Ryan 2026-05-21: chip + separator dropped from combined
             entries — the pill-pair on the row above already labels
             the entry, and reps found "💬 NOTE — Put on hold for X"
             read as noisy duplication next to "(OFF HOLD) → (ON
             HOLD)". Plain (non-transition) notes still get the chip
             below. */
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-07) — Split the call-
             outcome pill and the note onto separate rows. Was one inline
             flex div ([pill][text]) which pushed the note to the right of
             the pill and indented its wrapped lines (sloppy). Now the pill
             keeps its own row and the note drops full-width below it with
             no indent — mirrors the non-transition call branch. Pure
             transition notes (no pill) render exactly as before: just the
             text row. */
          /* ⚠️ The 2026-06-16 note that used to sit here — "restore the type chip on CALL entries
             that also carry a transition" — is obsolete: a call now takes the `_isCall` branch
             above, which puts the chip and the pills on ONE row. This branch is a transition with
             a note and no call, and stays chip-less as it has since 2026-05-21. */
          // Ryan 2026-07-11: only emit the text row when there IS text — a transition-only
          // entry (e.g. a prospect stage flip with no note) otherwise rendered an empty line.
          if (e.text && String(e.text).trim()) html += '<div class="rqd-notes-log__entry-line">' + _entryTextHtml + '</div>';
        } else {
          html += '<div class="rqd-notes-log__entry-meta"><span class="' + _chipCls + '">' + emojiStr + ' ' + esc(labelStr) + '</span>' + '<span class="rqd-notes-log__entry-meta-sep"> — </span>' + _entryTextHtml + '<span class="rqd-notes-log__entry-when">' + meta + '</span><span class="rqd-notes-log__entry-mutate-btns">' + mutateBtns + '</span></div>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Surface
           entry.followUpAt below the note body so the activity log
           actually shows that this note set a wake date. Worker
           already stamps followUpAt on the entry (lead-routes.js
           L2137) — the dashboard was just dropping it. On lead-state
           rows the transition pill-pair conveys "→ ON HOLD" but
           doesn't show the SPECIFIC date or duration; this label
           complements that with the concrete "X days, due <date>".
           On pending rows there's no pill-pair, so this label is
           the ONLY visible record that a follow-up was attached.
           Renders for both paths. */
        if (e.followUpAt) {
          var _fuLabel;
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Delta-
             aware variant. When the entry has previousFollowUpAt
             stamped (rep edited an existing hold's wake date),
             render "Follow-up increased/decreased. Was at Xd Yh,
             set for Yd — Date" instead of the plain "set for Xd".
             Direction derived from new vs old date comparison.
             If the date didn't actually change, fall back to the
             simple label (edge case — rep saved without changing
             the date). */
          if (e.previousFollowUpAt) {
            var _oldMs = Date.parse(e.previousFollowUpAt + 'T08:00:00Z');
            var _newMs = Date.parse(e.followUpAt + 'T08:00:00Z');
            var _direction = _newMs > _oldMs ? 'increased' : (_newMs < _oldMs ? 'decreased' : '');
            if (_direction) {
              var _oldStr = formatFollowUpRemaining(e.ts, e.previousFollowUpAt);
              var _newStr = formatFollowUpRemaining(e.ts, e.followUpAt);
              _fuLabel = _FU_LABEL + ' ' + _direction + '. Was at ' + _oldStr + ', now set for ' + _newStr + ' - ' + esc(formatFollowUpDate(e.followUpAt));
            }
          }
          if (!_fuLabel) {
            var _fuDays = _followUpDayCount(e.ts, e.followUpAt);
            _fuLabel = _FU_LABEL + ' Set for ' + _fuDays + 'd';
            if (_fuDays > 0) _fuLabel += ' - ' + esc(formatFollowUpDate(e.followUpAt));
          }
          html += '<div class="rqd-notes-log__entry-followup" title="Wake date ' + esc(e.followUpAt) + '">' + _fuLabel + '</div>';
        }
        html += '</div>';
      });
      // Close the hidden-entries wrapper if it was opened above.
      if (_hiddenOpen) html += '</div>';
      /* Ryan 2026-10-10: no "N hidden · Show everything" line; the Rep view / Everything switch in
         the DEAL FEED title is the way to see the rest. */
    }
    html += '</div>'; // .rqd-notes-log__entries
    html += '</div>'; // .rqd-notes-log__feed (left 2/3 of bottom row)

    // Right 1/3 of bottom row: LINKED FILES. Suppressed for prospects — floor-plan
    // asset linking is an offline-deal concept; the feed goes full-width instead
    // (.rqd-notes-log__split--no-sidecol).
    if (!ctx.isProspect) {
    html += '<div class="rqd-notes-log__sidecol">';
    html += '<div class="rqd-notes-log__linked-files">';
    html +=   '<div class="rqd-notes-log__sidecol-title">LINKED FILES</div>';
    if (Array.isArray(q.linkedFiles) && q.linkedFiles.length > 0) {
      q.linkedFiles.forEach(function(f) {
        if (!f) return;
        var fName = f.name || f.designRef || 'Floor plan';
        var fSub = f.spaceSummary || '';
        var fRef = f.designRef || '';
        var fUrl = f.designUrl || '#';
        var fMissing = !!f.missing;
        var missingCls = fMissing ? ' rqd-linked-file--missing' : '';
        var clickAttr = fMissing ? ' onclick="event.preventDefault(); return false;"' : '';
        var titleAttr = fMissing ? ' title="This design was removed"' : ' title="Open ' + esc(fRef || fName) + ' in the Gym Layout Designer"';
        /* Ryan 2026-05-21: card collapsed to single-line for height
           parity with the Save button. Title attribute carries the
           full ref + space summary on hover. Date renders inline on
           the right so the rep can see when the design was last
           touched (matches the date the Gym Layout Designer shows in
           its saved-files list). */
        var fDateLabel = f.updatedAt ? formatDate(f.updatedAt) : '';
        var hoverDetail = fMissing
          ? 'This design was removed'
          : (fName + (fSub ? ' — ' + fSub : '') + (fRef ? ' · ' + fRef : '') + (fDateLabel ? ' · updated ' + fDateLabel : ''));
        html += '<a class="rqd-linked-file' + missingCls + '" target="_blank" rel="noopener" href="' + esc(fUrl) + '"' + clickAttr + ' title="' + esc(hoverDetail) + '">';
        html +=   '<span class="rqd-linked-file__icon" aria-hidden="true">&#128208;</span>';
        /* Ryan 2026-05-21: viewport/scroller wrap for marquee. Outer
           .rqd-linked-file__name is the viewport (overflow:hidden).
           Inner .rqd-linked-file__name-text holds the full text — gets
           translated by the hover handler so the cut-off portion
           scrolls into view. Without the wrap, translating the
           ellipsis-truncated element just slides the same truncated
           string around without revealing the hidden text. */
        html +=   '<span class="rqd-linked-file__name">'
              +     '<span class="rqd-linked-file__name-text">' + esc(fName) + (fMissing ? ' <span class="rqd-linked-file__missing-tag">(removed)</span>' : '') + '</span>'
              +   '</span>';
        if (fDateLabel) html += '<span class="rqd-linked-file__date">' + esc(fDateLabel) + '</span>';
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — WHOSE DESIGN THIS IS, under the date.
           A deal now carries the customer's own submission AND the versions we made from it, and
           two rows that differ only by a revision letter are two rows nobody can tell apart. Ryan:
           *"if it's the design the customer did... add a small 'CUSTOMER' pill. If it's one we
           designed, add a 'OURS'."*

           ⚠️ ONLY ON WEIGHT DESIGNS, and only once `authored` is known. A floor plan has no such
           distinction, and a weights link written before adopting existed carries nothing — the
           worker falls back to the store (`wd-share` IS the customer's by definition), so a bare
           `wd-design` from before this shipped shows no pill rather than a guessed one. Saying
           nothing beats labelling somebody's work as ours. */
        if (f.type === 'weights' && f.authored) {
          var _isTheirs = f.authored === 'customer';
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — "MODIFIED DESIGN 1", "MODIFIED
             DESIGN 2", numbered off the revision letter rather than a bare "OURS". Ryan asked for
             the same wording the Weight Designer's own save menu uses, so a rep reading the deal and
             a rep reading the designer see one vocabulary.

             ⚠️ THE LETTER ITSELF, NOT A NUMBER (Ryan 2026-09-02): *"should say 'MODIFIED VERSION X'
             (X = B/C/D etc.)"*. It read "MODIFIED DESIGN 2" for `WEIGHT-1014-C`, which is the same
             design under a name that appears nowhere else — not on the file, not in the ref, not in
             the customer's header. The letter is what the rep can match against everything else.
             It also retires the deliberate off-by-one: nothing has to be explained now. */
          var _rev = String(f.designRef || '').split('-').pop() || '';
          var _label = _isTheirs
            ? 'CUSTOMER DESIGN'
            : (/^[B-Z]$/.test(_rev) ? 'MODIFIED VERSION ' + _rev : 'OURS');
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ AND WHEN THEY LAST CHANGED IT. A
             buyer can now revise their own design from the link we sent them, which rewrites the
             record this card opens. A card that has quietly changed under the rep, showing the same
             name and the same date it always did, is worse than no card: the rep would send a quote
             against a drawing they had not seen.
             ⚠️ THEIRS ONLY, because only theirs is revisable. And absent until somebody actually
             edits, so a submission nobody has touched reads exactly as it always has. */
          if (_isTheirs && f.revisedAt) {
            var _rd = new Date(f.revisedAt);
            if (!isNaN(_rd)) {
              _label += ' \u00b7 UPDATED ' + (_rd.getMonth() + 1) + '/' + _rd.getDate();
            }
          }
          html += '<span class="rqd-linked-file__who' + (_isTheirs ? ' rqd-linked-file__who--customer' : '') + '">'
                +   _label
                + '</span>';
        }
        html += '</a>';
      });
    } else {
      html += '<div class="rqd-linked-file__empty">No files linked yet.</div>';
    }
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — Asset linking
       controls. Renamed "Link Floor Plan" → "Link Asset" since
       non-floor-plan asset types (renderings, equipment lists,
       etc.) will land here later. When at least one asset is
       already linked, the Link Asset button shrinks to 2/3 width
       + an "Unlink Asset" button shows next to it at 1/3 width
       so reps can detach files cleanly. Buttons share the same
       visual treatment as the date picker + Save in the Follow Up
       cluster (also 2/3 + 1/3) for portal-wide consistency. */
    var _hasLinkedFiles = Array.isArray(q.linkedFiles) && q.linkedFiles.length > 0;
    html += '<div class="rqd-linked-file__actions' + (_hasLinkedFiles ? ' rqd-linked-file__actions--has-files' : '') + '">';
    html +=   '<button type="button" class="rqd-linked-file__add" data-ref="' + esc(q.reference) + '" title="Link an existing asset to this deal">Link Asset</button>';
    if (_hasLinkedFiles) {
      html += '<button type="button" class="rqd-linked-file__unlink" data-ref="' + esc(q.reference) + '" title="Remove a linked asset from this deal">Unlink</button>';
    }
    html += '</div>';
    html += '</div>'; // .rqd-notes-log__linked-files
    /* CUSTOM_AGENTOWNER_BY_CLAUDE (Ryan 2026-10-17) - the deal's AI agent, at the bottom of this column. */
    if (window.RQD && window.RQD.buildAgentOwnerChip) html += window.RQD.buildAgentOwnerChip(q);
    html += '</div>'; // .rqd-notes-log__sidecol
    } // end if (!ctx.isProspect) — linked-files sidecol
    html += '</div>'; // .rqd-notes-log__split

    html += '</div>'; // .rqd-detail__card.rqd-notes-log
    return html;
  }

  function initActivityCard(deps) {
    deps = deps || {};
    esc = deps.esc;
    formatNoteDate = deps.formatNoteDate;
    formatFollowUpDate = deps.formatFollowUpDate;
    formatFollowUpRemaining = deps.formatFollowUpRemaining;
    formatDate = deps.formatDate;
    _followUpDayCount = deps._followUpDayCount;
    daysFromNowYmd = deps.daysFromNowYmd;
    collapsedSections = deps.collapsedSections || {};
    getIsAdmin = deps.getIsAdmin || function() { return false; };
    fetchQuotes = deps.fetchQuotes || null;
  }

  /* The block's Show/Stop buttons. Installed once, delegated on document — see
     _wireSequenceBlock for why it cannot be bound per render. */
  _wireSequenceBlock();
  _wireCallWaitBlock(); // CUSTOM_CALLWAIT_BY_CLAUDE

  window.RQD.clearSequenceBlock = clearSequenceBlock;
  window.RQD.clearCallWaitBlock = clearCallWaitBlock; // CUSTOM_CALLWAIT_BY_CLAUDE
  window.RQD.buildActivityCard = buildActivityCard;
  window.RQD.initActivityCard = initActivityCard;
})();
